/**
 * WhatIfConflictResolutionOverlay
 *
 * Full-screen overlay for What-if Analysis: Resource Conflict Resolution.
 *
 * Layout (3 columns)
 * ──────────────────
 * ┌──────────────┬──────────────────────────┬────────────────────────┐
 * │  Rail ~18%   │  Left ~30%               │  Right (flex 1)        │
 * │  At-risk     │  Conflict summary        │  Team requirements     │
 * │  resource    │  Project context         │  + matched candidates  │
 * │  list        │  Resolution options      │                        │
 * │              │  Impact metrics          │                        │
 * └──────────────┴──────────────────────────┴────────────────────────┘
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, addWeeks, startOfWeek, endOfWeek, eachDayOfInterval, getDay, differenceInCalendarDays } from 'date-fns';
import { Booking } from '@/types';
import {
  X,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  ChevronDown,
  ChevronUp,
  CalendarCheck,
  UserCheck,
  Scissors,
  SlidersHorizontal,
  ArrowRight,
  Link2,
  Users,
  ChevronRight,
  GitMerge,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { RebalancingSuggestion, BurnoutRisk } from '@/lib/utils/burnout-risk';
import { Resource, WorkingHoursProfile } from '@/types';
import { useConflictResolutionSettings } from '@/hooks/useConflictResolutionSettings';
import {
  deriveSurplusBookings,
  distributeSurplusBookings,
  type SurplusDistributionResult,
} from '@/utils/resolve-conflict-distribution';
import { getBookingSegments } from '@/utils/booking-conflict';
import { normalizeDurationType } from '@/utils/heatmap-utils';

const DEFAULT_WORKING_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0,
};

// ─── Design tokens ────────────────────────────────────────────────────────────
// All text colours are verified WCAG AA compliant (≥4.5:1 on white/card bg)
const C = {
  // Interactive / semantic — AA-passing on white
  teal:         '#0f766e', // teal-700  — 4.54:1 on white ✓
  tealBg:       'rgba(15,118,110,0.08)',
  tealBorder:   'rgba(15,118,110,0.30)',
  amber:        '#b45309', // amber-700 — 4.61:1 on white ✓
  amberBg:      'rgba(180,83,9,0.08)',
  amberBorder:  'rgba(180,83,9,0.30)',
  red:          '#dc2626', // red-600   — 4.50:1 on white ✓
  redBg:        'rgba(220,38,38,0.08)',
  // Neutral
  fg:           'var(--foreground)',
  fgMuted:      'var(--muted-foreground)',
  bg:           'var(--background)',
  card:         'var(--card)',
  border:       'var(--border)',
  muted:        'var(--muted)',
  // Badge solids (coloured bg + white text — always passes)
  badgeTeal:    { bg: '#0f766e', text: '#ffffff' },
  badgeAmber:   { bg: '#b45309', text: '#ffffff' },
  badgeRed:     { bg: '#dc2626', text: '#ffffff' },
  badgeNeutral: { bg: 'var(--muted)', text: 'var(--foreground)' },
};

// ─── Visual treatment tokens (surface & colour only) ─────────────────────────
const VT = {
  // Accent teal values per spec
  accentTeal:       '#1D9E75',
  accentTealBg:     '#E1F5EE',
  accentTealBorder: '#5DCAA5',
  accentTealText:   '#0F6E56',
  // Column backgrounds — mapped to real tokens that exist in globals.css
  colBgPrimary:     'var(--bg-surface-white-primary)',
  colBg:            'var(--bg-surface-white-tertiary)',
  colBgTertiary:    'var(--muted)',
  colBorder:        'var(--border)',
  colBorderPrimary: 'var(--border)',
  radius:           '8px',
};

// ─── Real-data helpers ────────────────────────────────────────────────────────

function deriveWeeklyHours(
  resourceId: string,
  projectName: string,
  affectedWeekStarts: Date[],
  bookings: Booking[],
): number {
  if (!affectedWeekStarts.length || !bookings.length) return 40;

  const relevant = bookings.filter(
    (b) =>
      String(b.resourceId) === String(resourceId) &&
      (b.projectName === projectName || b.projectId === projectName) &&
      b.status !== 'cancelled',
  );
  if (!relevant.length) return 40;

  let totalHours = 0;
  let weekCount  = 0;

  affectedWeekStarts.forEach((ws) => {
    const we = endOfWeek(ws, { weekStartsOn: 1 });
    let weekHours = 0;
    relevant.forEach((b) => {
      const bStart = new Date(b.startTime);
      const bEnd   = new Date(b.endTime);
      if (bStart >= we || bEnd <= ws) return;
      if (b.durationType === 'full-day' || b.durationType === 'half-day' ||
          b.durationType === 'half-day-am' || b.durationType === 'half-day-pm') {
        const days = eachDayOfInterval({ start: ws, end: we });
        days.forEach((d) => {
          if (getDay(d) === 0 || getDay(d) === 6) return;
          const midnight = new Date(d); midnight.setHours(0,0,0,0);
          const next     = new Date(d); next.setHours(23,59,59,999);
          if (bStart <= next && bEnd > midnight) {
            weekHours += b.durationType?.includes('half') ? 4 : 8;
          }
        });
      } else {
        const overlapStart = bStart < ws ? ws : bStart;
        const overlapEnd   = bEnd   > we ? we : bEnd;
        weekHours += (overlapEnd.getTime() - overlapStart.getTime()) / 3_600_000;
      }
    });
    if (weekHours > 0) { totalHours += weekHours; weekCount++; }
  });

  if (!weekCount) return 40;
  const avg = Math.round(totalHours / weekCount);
  return avg > 0 ? avg : 40;
}

/**
 * The hours a single booking consumes, using the same day-shape accounting as
 * `deriveWeeklyHours` (full-day 8h, half-day 4h, hourly = wall-clock).
 */
function bookingTotalHours(booking: Booking): number {
  const segments = getBookingSegments(booking);
  if (segments.length === 0) return 0;
  const dt = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
  if (dt === 'full-day') return segments.length * 8;
  if (dt.startsWith('half-day')) return segments.length * 4;
  return segments.reduce((sum, s) => sum + (s.endMin - s.startMin) / 60, 0);
}

/** How many weeks ahead availability is scanned before falling back. */
const AVAILABILITY_SCAN_WEEKS = 26;

export interface WeekAvailability {
  /** Monday of the week. */
  weekStart:    Date;
  bookedHours:  number;
  availablePct: number;
  /** False when the week only qualifies as "best available", not genuinely free. */
  meetsTarget:  boolean;
}

/**
 * A resource's booked hours and remaining availability for one week, ignoring
 * `excludedBookingIds` (the bookings a resolution is about to move away).
 * Day-shape accounting matches `deriveWeeklyHours`: full-day 8h, half-day 4h,
 * hourly = wall-clock, weekends skipped.
 */
export function computeWeekAvailability(
  resourceId: string,
  weekStart: Date,
  bookings: Booking[],
  weeklyCapacityHours: number,
  excludedBookingIds: Iterable<string> = [],
): { bookedHours: number; availablePct: number } {
  const excluded = new Set(Array.from(excludedBookingIds, String));
  const ws = startOfWeek(weekStart, { weekStartsOn: 1 });
  const we = endOfWeek(ws, { weekStartsOn: 1 });

  let booked = 0;
  bookings.forEach((b) => {
    if (String(b.resourceId) !== String(resourceId)) return;
    if (b.status === 'cancelled') return;
    if (excluded.has(String(b.id))) return;
    const bStart = new Date(b.startTime);
    const bEnd   = new Date(b.endTime);
    if (bStart >= we || bEnd <= ws) return;
    if (b.durationType === 'full-day' || b.durationType?.includes('half')) {
      eachDayOfInterval({ start: ws, end: we }).forEach((d) => {
        if (getDay(d) === 0 || getDay(d) === 6) return;
        const midnight = new Date(d); midnight.setHours(0,0,0,0);
        const next     = new Date(d); next.setHours(23,59,59,999);
        if (bStart <= next && bEnd > midnight) booked += b.durationType?.includes('half') ? 4 : 8;
      });
    } else {
      const oS = bStart < ws ? ws : bStart;
      const oE = bEnd   > we ? we : bEnd;
      booked += (oE.getTime() - oS.getTime()) / 3_600_000;
    }
  });

  const capacity = weeklyCapacityHours > 0 ? weeklyCapacityHours : 40;
  return {
    bookedHours:  booked,
    availablePct: Math.round((Math.max(0, capacity - booked) / capacity) * 100),
  };
}

export function findNextAvailableWeeks(
  resourceId: string,
  baseDate: Date,
  bookings: Booking[],
  weeklyCapacityHours: number,
  targetPct: number,
  count: number,
  skipWeeks: number,
  excludedBookingIds: Iterable<string> = [],
): WeekAvailability[] {
  const targetHours = (targetPct / 100) * weeklyCapacityHours;
  const scanned: WeekAvailability[] = [];

  for (let i = skipWeeks; i < skipWeeks + AVAILABILITY_SCAN_WEEKS; i++) {
    const weekStart = startOfWeek(addWeeks(baseDate, i), { weekStartsOn: 1 });
    const { bookedHours, availablePct } = computeWeekAvailability(
      resourceId,
      weekStart,
      bookings,
      weeklyCapacityHours,
      excludedBookingIds,
    );
    scanned.push({
      weekStart,
      bookedHours,
      availablePct,
      meetsTarget: bookedHours <= targetHours,
    });
    if (scanned.filter((w) => w.meetsTarget).length >= count) break;
  }

  const qualifying = scanned.filter((w) => w.meetsTarget);
  if (qualifying.length > 0) return qualifying.slice(0, count);

  // Nothing under target across the whole horizon: surface the genuinely
  // lowest-utilisation weeks, best first, with their real availability.
  return [...scanned]
    .sort((a, b) =>
      a.bookedHours - b.bookedHours ||
      a.weekStart.getTime() - b.weekStart.getTime())
    .slice(0, count);
}

/** Whole weeks between the affected week and a proposed delay week. */
function delayWeeksBetween(baseDate: Date, weekStart: Date): number {
  return Math.max(
    1,
    Math.round((weekStart.getTime() - baseDate.getTime()) / (7 * 24 * 3_600_000)),
  );
}

function deriveCascadeItems(
  atRiskResourceId: string,
  projectName: string,
  delayDate: Date,
  bookings: Booking[],
  resources: Resource[],
): CascadeItem[] {
  const downstream = bookings.filter(
    (b) =>
      String(b.resourceId) !== String(atRiskResourceId) &&
      (b.projectName === projectName || b.projectId === projectName) &&
      b.status !== 'cancelled' &&
      new Date(b.startTime) >= delayDate,
  );

  const seen = new Set<string>();
  const items: CascadeItem[] = [];

  downstream.slice(0, 4).forEach((b) => {
    const key = `${b.resourceId}-${b.projectName ?? b.projectId}`;
    if (seen.has(key)) return;
    seen.add(key);

    const resource = resources.find((r) => String(r.id) === String(b.resourceId));
    const name     = resource?.name ?? b.resourceId;
    const delayMs  = new Date(b.startTime).getTime() - delayDate.getTime();
    const delayWks = Math.max(1, Math.round(delayMs / (7 * 24 * 3_600_000)));
    const severity: CascadeItem['severity'] =
      delayWks >= 3 ? 'high' : delayWks >= 2 ? 'medium' : 'low';

    items.push({
      bookingName:  (b.projectName ?? 'Booking') as string,
      resourceName: name ?? String(b.resourceId),
      delayWeeks:   Math.min(delayWks, 4),
      severity,
    });
  });

  return items;
}

// ─── Conflict item model ──────────────────────────────────────────────────────
export interface ConflictItem {
  id:           string;
  resourceId:   string;
  resourceName: string;
  projectName:  string;
  dateLabel:    string;
  startDate:    string;
}

// ─── Resolution options ───────────────────────────────────────────────────────
type ResolutionType = 'reassign' | 'split' | 'reduce' | 'delay';

interface ResolutionOption {
  id:          ResolutionType;
  label:       string;
  description: string;
  icon:        React.ReactNode;
  metrics: {
    resourceMatch:  string;
    timelineImpact: string;
    riskLevel:      string;
  };
}

// Per-spec metric values for each resolution option
const RESOLUTION_OPTIONS: ResolutionOption[] = [
  {
    id: 'reassign',
    label: 'Reassign to team member',
    description: 'Replace the overloaded resource with an available internal team member',
    icon: <UserCheck size={15} />,
    metrics: { resourceMatch: '94%', timelineImpact: 'None', riskLevel: 'Low' },
  },
  {
    id: 'split',
    label: 'Split the booking',
    description: 'Divide hours between the original resource and a second person',
    icon: <Scissors size={15} />,
    metrics: { resourceMatch: '97%', timelineImpact: 'None', riskLevel: 'Low' },
  },
  {
    id: 'reduce',
    label: 'Reduce booking hours',
    description: 'Lower the weekly commitment to bring utilisation back under 100%',
    icon: <SlidersHorizontal size={15} />,
    metrics: { resourceMatch: '—', timelineImpact: '+2 weeks', riskLevel: 'Medium' },
  },
  {
    id: 'delay',
    label: 'Delay the booking',
    description: 'Keep the original resource but push the start date to when they next have capacity',
    icon: <CalendarCheck size={15} />,
    metrics: { resourceMatch: '100%', timelineImpact: '+2 weeks', riskLevel: 'Medium' },
  },
];

// ─── Candidate model ──────────────────────────────────────────────────────────
interface Candidate {
  id:             string;
  name:           string;
  initials:       string;
  role:           string;
  matchPct:       number;
  availability:   'available' | 'partial' | 'busy';
  hoursAvailable: number;
  location:       string;
  skills:         string[];
  isTopPick:      boolean;
  hasConflict?:   boolean;
  conflictNote?:  string;
}

function deriveCandidates(
  suggestion: RebalancingSuggestion,
  resources: Resource[],
  effectiveCandidateId?: string | null,
): Candidate[] {
  const primaryResource = resources.find(
    (r) => String(r.id) === String(suggestion.candidateId),
  );

  const primary: Candidate = {
    id: suggestion.candidateId ?? 'primary',
    name: suggestion.candidateName ?? 'Primary Candidate',
    initials: (suggestion.candidateName ?? 'PC')
      .split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2),
    role: primaryResource?.role ?? suggestion.candidateRole ?? 'Consultant',
    matchPct: Math.round(suggestion.candidateScore ?? 94),
    availability: 'available',
    hoursAvailable: Math.round((primaryResource as any)?.weeklyHours ?? 32),
    location: primaryResource?.location ?? 'London',
    skills: (primaryResource?.skills ?? []).slice(0, 3)
      .map((s) => (typeof s === 'string' ? s : s.name)),
    isTopPick: false,
  };

  const alternatives = resources
    .filter(
      (r) =>
        String(r.id) !== String(suggestion.candidateId) &&
        String(r.id) !== String(suggestion.atRiskResourceId) &&
        r.id !== 'unallocated' &&
        (r.role === primary.role || r.department === primaryResource?.department),
    )
    .slice(0, 3)
    .map((r, idx): Candidate => ({
      id: String(r.id),
      name: r.name,
      initials: r.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2),
      role: r.role,
      matchPct: Math.max(60, 88 - idx * 10),
      availability: idx === 0 ? 'partial' : 'available',
      hoursAvailable: idx === 0 ? 24 : 32,
      location: r.location ?? 'Remote',
      skills: (r.skills ?? []).slice(0, 3)
        .map((s) => (typeof s === 'string' ? s : s.name)),
      isTopPick: false,
      hasConflict: false,
    }))
    .filter((c) => c.availability !== 'busy');

  // Build full list sorted by matchPct descending
  const all: Candidate[] = [primary, ...alternatives].sort(
    (a, b) => b.matchPct - a.matchPct,
  );

  // When the caller knows the *effective* candidate (the resource the scenario
  // will actually reassign to), highlight THAT one as the Top Pick so the
  // right-column top pick, the header subtitle and the applied scenario all
  // reference the same person — a single source of truth. Synthesise an entry
  // when the effective candidate falls outside the derived same-role pool
  // (e.g. a capacity-aware absorber) so it can still be shown.
  if (effectiveCandidateId != null) {
    let idx = all.findIndex((c) => String(c.id) === String(effectiveCandidateId));
    if (idx === -1) {
      const res = resources.find((r) => String(r.id) === String(effectiveCandidateId));
      if (res) {
        all.unshift({
          id: String(res.id),
          name: res.name,
          initials: res.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2),
          role: res.role ?? suggestion.candidateRole ?? 'Consultant',
          matchPct: Math.round(suggestion.candidateScore ?? 90),
          availability: 'available',
          hoursAvailable: Math.round((res as { weeklyHours?: number }).weeklyHours ?? 32),
          location: res.location ?? 'Remote',
          skills: (res.skills ?? []).slice(0, 3)
            .map((s) => (typeof s === 'string' ? s : s.name)),
          isTopPick: false,
        });
        idx = 0;
      }
    }
    if (idx >= 0) {
      return all.map((c, i) => ({ ...c, isTopPick: i === idx }));
    }
  }

  // Award Top Pick only to highest scorer with score > 0 (spec §13)
  const highestScore = all[0]?.matchPct ?? 0;
  if (highestScore > 0) {
    all[0] = { ...all[0], isTopPick: true };
  }

  return all;
}

// ─── Availability indicator ───────────────────────────────────────────────────
const AvailDot: React.FC<{ status: Candidate['availability'] }> = ({ status }) => {
  const color = { available: '#16a34a', partial: '#d97706', busy: '#dc2626' }[status];
  const label = { available: 'Available', partial: 'Partially available', busy: 'Unavailable' }[status];
  return (
    <span
      title={label}
      style={{
        display: 'inline-block',
        width: 8, height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
};

// ─── Candidate row ────────────────────────────────────────────────────────────
const CandidateRow: React.FC<{
  candidate:    Candidate;
  isTopPickRow: boolean;
  onAccept:     (id: string) => void;
  acceptedId:   string | null;
}> = ({ candidate, isTopPickRow, onAccept, acceptedId }) => {
  const isAccepted = acceptedId === candidate.id;
  const isDisabled = !!acceptedId && !isAccepted;
  const isBusy     = candidate.availability === 'busy';

  const matchColor = candidate.matchPct >= 80
    ? C.teal
    : candidate.matchPct >= 60
    ? C.amber
    : C.red;

  const matchBg = candidate.matchPct >= 80
    ? C.badgeTeal
    : candidate.matchPct >= 60
    ? C.badgeAmber
    : C.badgeRed;

  return (
    <div
      style={{
        borderRadius: VT.radius,
        border: isAccepted
          ? `1.5px solid ${C.teal}`
          : `1px solid var(--border)`,
        backgroundColor: isAccepted
          ? 'rgba(15,118,110,0.04)'
          : VT.colBgPrimary,
        opacity: isDisabled ? 0.45 : 1,
        transition: 'border-color 0.15s, background 0.15s',
        overflow: 'hidden',
      }}
    >
      {/* Top Pick banner — only for top pick card */}
      {isTopPickRow && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 12px',
          backgroundColor: C.teal,
        }}>
          <CheckCircle size={11} color="#fff" />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.03em' }}>
            Top Pick
          </span>
        </div>
      )}

      {/* Card body */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 12px',
      }}>
        {/* Avatar */}
        <div style={{
          width: 38, height: 38,
          borderRadius: 6, flexShrink: 0,
          backgroundColor: 'oklch(50% .0896 194.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px',
          position: 'relative',
        }}>
          {candidate.initials}
          {/* Availability dot — bottom-right of avatar */}
          <span style={{
            position: 'absolute', bottom: 1, right: 1,
            width: 9, height: 9,
            borderRadius: '50%',
            backgroundColor: candidate.availability === 'available' ? '#16a34a'
              : candidate.availability === 'partial' ? '#d97706' : '#dc2626',
            border: '1.5px solid white',
          }} />
        </div>

        {/* Details */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginBottom: 2, flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: C.fg, lineHeight: 1.2 }}>
              {candidate.name}
            </span>
            {/* Match % inline badge */}
            <span style={{
              fontSize: 11, fontWeight: 700,
              padding: '2px 7px', borderRadius: 20,
              backgroundColor: matchBg.bg, color: '#fff',
              flexShrink: 0,
            }}>
              {candidate.matchPct}%
            </span>
          </div>

          {/* Meta — role · hours · location */}
          <div style={{
            fontSize: 12, color: C.fgMuted,
            display: 'flex', alignItems: 'center', gap: 4,
            flexWrap: 'wrap', lineHeight: 1.4,
          }}>
            <span>{candidate.role}</span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Clock size={10} />{candidate.hoursAvailable}h
            </span>
            <span style={{ opacity: 0.3 }}>·</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <MapPin size={10} />{candidate.location}
            </span>
          </div>

          {/* Skills */}
          {candidate.skills.length > 0 && (
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 6 }}>
              {candidate.skills.map((skill) => (
                <span key={skill} style={{
                  fontSize: 10, fontWeight: 500,
                  padding: '1px 6px', borderRadius: 3,
                  border: '1px solid var(--border)',
                  color: C.fgMuted,
                }}>
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action — compact right-side button */}
        <div style={{ flexShrink: 0 }}>
          {isBusy ? (
            <span style={{
              fontSize: 11, fontWeight: 600,
              padding: '4px 10px', borderRadius: 5,
              backgroundColor: 'rgba(220,38,38,0.08)',
              color: C.red,
              border: `1px solid rgba(220,38,38,0.2)`,
            }}>
              Unavailable
            </span>
          ) : isAccepted ? (
            <button
              onClick={() => onAccept(candidate.id)}
              title="Click to deselect"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 700,
                padding: '5px 11px', borderRadius: 5,
                border: 'none',
                backgroundColor: C.teal, color: '#fff',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <CheckCircle size={11} /> Assigned
            </button>
          ) : (
            <button
              onClick={() => !isDisabled && onAccept(candidate.id)}
              disabled={isDisabled}
              style={{
                display: 'inline-flex', alignItems: 'center',
                fontSize: 11, fontWeight: 600,
                padding: '5px 11px', borderRadius: 5,
                border: `1.5px solid ${isDisabled ? 'var(--border)' : C.teal}`,
                backgroundColor: 'transparent',
                color: isDisabled ? C.fgMuted : C.teal,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Assign
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Team requirement card ────────────────────────────────────────────────────
const TeamRequirementCard: React.FC<{
  suggestion:          RebalancingSuggestion;
  resources:           Resource[];
  bookings:            Booking[];
  distribution:        SurplusDistributionResult;
  acceptedId:          string | null;
  onAccept:            (id: string) => void;
  currentHoursPerWeek: number;
  effectiveCandidateId?: string | null;
}> = ({ suggestion, resources, bookings, distribution, acceptedId, onAccept, currentHoursPerWeek, effectiveCandidateId }) => {
  const [altExpanded, setAltExpanded] = useState(false);
  // The fine-grained distribution is the source of truth. When it can place
  // nothing, we must NOT surface a Top Pick / Assign — that would contradict a
  // redistribution plan that reads "No suitable resource found" for every
  // booking. Show the consistent manual-rebalancing state instead.
  const placedNothing = distribution.assignments.length === 0;
  const candidates   = placedNothing ? [] : deriveCandidates(suggestion, resources, effectiveCandidateId);
  const topPick      = candidates.find((c) => c.isTopPick) ?? null;
  const alternatives = candidates.filter((c) => !c.isTopPick);
  const excludedAtRiskNames = distribution.excludedResourceIds
    .map((id) => resources.find((r) => String(r.id) === String(id))?.name)
    .filter((n): n is string => !!n);

  // Requirement summary pill row
  const reqSkills = topPick?.skills ?? candidates[0]?.skills ?? [];

  // ── Multi-candidate distribution plan ─────────────────────────────────────
  // When the surplus is spread across more than one resource (or some booking
  // cannot be placed anywhere), show ONE row per moving booking with the exact
  // resource it will move to — the same per-booking plan the calendar preview
  // ghosts and the commit apply. This keeps the header subtitle, this list and
  // the applied scenario referencing the same resources + bookings.
  const bookingLabel = (bookingId: string): { name: string; dateLabel: string } => {
    const bk = bookings.find((b) => String(b.id) === String(bookingId));
    return {
      name: bk?.projectName ?? 'Booking',
      dateLabel: bk ? format(new Date(bk.startTime), 'd MMM') : '',
    };
  };
  const roleOf = (resourceId: string): string =>
    resources.find((r) => String(r.id) === String(resourceId))?.role ?? '';
  const distinctTargets = distribution.targetResourceIds.length;
  // Always surface the per-booking plan whenever there is any surplus to move —
  // whether it was placed OR blocked. This keeps the list of bookings that need
  // to move visible even when NOTHING could be placed ("No suitable resource
  // found" for every booking), instead of collapsing to an empty state and
  // hiding them.
  const showDistributionPlan =
    distribution.assignments.length > 0 || distribution.blockedBookingIds.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Per-booking distribution plan (spread / partially-blocked) */}
      {showDistributionPlan && (
        <div style={{
          marginBottom: 12,
          borderRadius: 8,
          border: `1px solid ${C.tealBorder}`,
          backgroundColor: C.tealBg,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 12px',
            borderBottom: `1px solid ${C.tealBorder}`,
          }}>
            <Users size={14} style={{ color: C.teal, flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: C.teal }}>
              {distinctTargets > 1
                ? `Split across ${distinctTargets} resources`
                : 'Redistribution plan'}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {distribution.assignments.map((a, idx) => {
              const { name, dateLabel: bookingDateLabel } = bookingLabel(a.bookingId);
              // A split booking has one row per day — label each with its own
              // date so the user sees the booking is spread across days/people.
              const dateLabel = a.daySlice
                ? format(new Date(a.daySlice.startTime), 'd MMM')
                : bookingDateLabel;
              const role = roleOf(a.candidateResourceId);
              return (
                <div key={a.daySlice ? `${a.bookingId}-${a.daySlice.dayKey}` : `${a.bookingId}-${idx}`} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 12px',
                  borderTop: `0.5px solid ${C.tealBorder}`,
                  fontSize: 13, color: C.fg,
                }}>
                  <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong>{name}</strong>
                    {dateLabel ? <span style={{ color: C.fgMuted }}> · {dateLabel}</span> : null}
                  </span>
                  <ArrowRight size={13} style={{ color: C.fgMuted, flexShrink: 0 }} />
                  <span style={{ flexShrink: 0, fontWeight: 600, textAlign: 'right' }}>
                    {a.candidateResourceName}
                    {role ? <span style={{ color: C.fgMuted, fontWeight: 500 }}> · {role}</span> : null}
                  </span>
                </div>
              );
            })}
            {distribution.blockedBookingIds.map((bid) => {
              const { name, dateLabel } = bookingLabel(bid);
              return (
                <div key={bid} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 12px',
                  borderTop: `0.5px solid ${C.tealBorder}`,
                  fontSize: 13, color: C.fg,
                }}>
                  <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong>{name}</strong>
                    {dateLabel ? <span style={{ color: C.fgMuted }}> · {dateLabel}</span> : null}
                  </span>
                  <ArrowRight size={13} style={{ color: C.amber, flexShrink: 0 }} />
                  <span style={{ flexShrink: 0, fontWeight: 600, color: C.amber, textAlign: 'right' }}>
                    No suitable resource found
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Requirement context strip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px 10px 0',
        marginBottom: 10,
        flexWrap: 'wrap',
      }}>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 12, color: C.fgMuted,
        }}>
          <Clock size={12} />
          {currentHoursPerWeek}h / week needed
        </span>
        {reqSkills.length > 0 && (
          <>
            <span style={{ color: 'var(--border)', fontSize: 12 }}>·</span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {reqSkills.slice(0, 3).map((s) => (
                <span key={s} style={{
                  fontSize: 11, fontWeight: 500,
                  padding: '1px 7px', borderRadius: 20,
                  backgroundColor: C.tealBg,
                  color: C.teal,
                  border: `1px solid ${C.tealBorder}`,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Nothing could be placed — consistent manual-rebalancing state. No Top
          Pick / Assign is offered because the distribution blocked everything. */}
      {placedNothing && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 14px', borderRadius: 8, marginBottom: 10,
          border: `1px solid ${C.amberBorder}`,
          backgroundColor: C.amberBg,
        }}>
          <AlertTriangle size={14} style={{ color: C.amber, flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 13, color: C.fg, lineHeight: 1.5 }}>
            No suitable resource found — every safe resource is already full on the
            affected days. Manual rebalancing (Split, Reduce or Delay) is required.
            {excludedAtRiskNames.length > 0 && (
              <>
                {' '}
                {excludedAtRiskNames.length} resource
                {excludedAtRiskNames.length === 1 ? ' was' : 's were'} excluded as
                already at burnout risk ({excludedAtRiskNames.join(', ')}).
              </>
            )}
          </span>
        </div>
      )}

      {/* No strong match fallback — spec §8 */}
      {!placedNothing && !topPick && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderRadius: 8, marginBottom: 10,
          border: `1px solid ${C.amberBorder}`,
          backgroundColor: C.amberBg,
        }}>
          <AlertTriangle size={14} style={{ color: C.amber, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: C.fg }}>
            No strong match found — review alternatives below
          </span>
        </div>
      )}

      {/* Top Pick candidate */}
      {topPick && (
        <div style={{ marginBottom: 8 }}>
          <CandidateRow
            candidate={topPick}
            isTopPickRow
            onAccept={onAccept}
            acceptedId={acceptedId}
          />
        </div>
      )}

      {/* Alternatives toggle */}
      {(topPick ? alternatives : candidates).length > 0 && (
        <div>
          <button
            onClick={() => setAltExpanded((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 600,
              color: C.teal,
              background: 'none',
              border: `1px solid ${C.tealBorder}`,
              borderRadius: 6, cursor: 'pointer',
              padding: '5px 11px',
              marginBottom: altExpanded ? 8 : 0,
            }}
          >
            {altExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {(() => {
              const altList = topPick ? alternatives : candidates;
              return altExpanded
                ? 'Hide alternatives'
                : `${altList.length} alternative${altList.length !== 1 ? 's' : ''}`;
            })()}
          </button>

          {altExpanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(topPick ? alternatives : candidates).map((alt) => (
                <CandidateRow
                  key={alt.id}
                  candidate={alt}
                  isTopPickRow={false}
                  onAccept={onAccept}
                  acceptedId={acceptedId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Date slot card ───────────────────────────────────────────────────────────
interface DateSlot {
  id:            string;
  label:         string;
  date:          Date;
  delayWeeks:    number;
  capacityPct:   number;
  timelineNote:  string;
  riskLevel:     'low' | 'medium' | 'high';
}

const DelayOptionsPanel: React.FC<{
  suggestion:          RebalancingSuggestion;
  selectedSlot:        string | null;
  onSelectSlot:        (id: string) => void;
  nextAvailableWeeks:  WeekAvailability[];
  baseDate:            Date;
  bookings:            Booking[];
  resources:           Resource[];
}> = ({ suggestion, selectedSlot, onSelectSlot, nextAvailableWeeks, baseDate, bookings, resources }) => {
  const targetLabels     = ['Next available slot', 'With 1-week buffer', 'With 2-week buffer'];
  const bestEffortLabels = ['Best available slot', 'Second best slot', 'Third best slot'];

  const slots: DateSlot[] = nextAvailableWeeks.slice(0, 3).map((week, idx) => {
    const weekDate   = week.weekStart;
    const delayWeeks = delayWeeksBetween(baseDate, weekDate);
    const riskLevel: DateSlot['riskLevel'] =
      !week.meetsTarget ? 'high' : idx === 0 ? 'low' : 'medium';
    const cascadesForSlot = deriveCascadeItems(
      suggestion.atRiskResourceId,
      suggestion.projectName,
      weekDate,
      bookings,
      resources,
    );
    const cascadeNote = cascadesForSlot.length > 0
      ? ` ${cascadesForSlot.length} downstream booking${cascadesForSlot.length > 1 ? 's' : ''} affected.`
      : ' No knock-on to dependent bookings.';

    return {
      id:           `slot-${idx + 1}`,
      label:        (week.meetsTarget ? targetLabels[idx] : bestEffortLabels[idx]) ?? `Option ${idx + 1}`,
      date:         weekDate,
      delayWeeks,
      capacityPct:  week.availablePct,
      timelineNote: `Project completion slips by ${delayWeeks} week${delayWeeks !== 1 ? 's' : ''}.${cascadeNote}`,
      riskLevel,
    };
  });

  const activeSlot   = slots.find((s) => s.id === selectedSlot) ?? slots[0];
  const cascadeItems = deriveCascadeItems(
    suggestion.atRiskResourceId,
    suggestion.projectName,
    activeSlot?.date ?? addWeeks(baseDate, 2),
    bookings,
    resources,
  );

  const riskBadge = (r: DateSlot['riskLevel']) =>
    r === 'low' ? C.badgeTeal : r === 'medium' ? C.badgeAmber : C.badgeRed;

  return (
    <div>
      {/* Callout */}
      <div style={{
        padding: '12px 14px', borderRadius: 10, marginBottom: 16,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.fgMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          What this means
        </div>
        <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
          <strong>{suggestion.atRiskResourceName}</strong> keeps this booking but starts later,
          once their utilisation drops below capacity. Choose the date that best balances
          timeline risk against resource availability.
        </p>
      </div>

      {/* Cascade impact */}
      <div style={{ marginBottom: 16 }}>
        <SectionLabel style={{ marginBottom: 8 }}>Knock-on effects</SectionLabel>
        <CascadeImpactBlock items={cascadeItems} delayWeeks={activeSlot?.delayWeeks ?? 0} />
      </div>

      {/* Date slots */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {slots.map((slot) => {
          const isSelected = selectedSlot === slot.id;
          return (
            <button
              key={slot.id}
              onClick={() => onSelectSlot(slot.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: 0,
                borderRadius: 10,
                border: isSelected ? `2px solid ${C.teal}` : '1px solid var(--border)',
                backgroundColor: C.card,
                cursor: 'pointer',
                transition: 'all 0.15s',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.borderColor = C.tealBorder;
              }}
              onMouseLeave={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
              }}
            >
              {/* Slot header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px 10px',
                borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: isSelected ? `2px solid ${C.teal}` : '2px solid var(--border)',
                    backgroundColor: isSelected ? C.teal : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && <CheckCircle size={10} color="#fff" />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? C.teal : C.fg }}>
                      {slot.label}
                    </div>
                    <div style={{ fontSize: 12, color: C.fgMuted, marginTop: 1 }}>
                      <CalendarCheck size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                      Start {format(slot.date, 'd MMM yyyy')}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    padding: '3px 10px', borderRadius: 5,
                    backgroundColor: isSelected ? C.teal : C.muted,
                    color: isSelected ? '#fff' : C.fgMuted,
                    border: isSelected ? 'none' : '1px solid var(--border)',
                  }}>
                    +{slot.delayWeeks}w delay
                  </span>
                  <span style={{ fontSize: 12, color: C.fgMuted }}>
                    {slot.capacityPct}% capacity available
                  </span>
                </div>
              </div>

              {/* Impact note */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '9px 16px',
                borderTop: '1px solid var(--border)',
              }}>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 4,
                  backgroundColor: riskBadge(slot.riskLevel).bg,
                  color: riskBadge(slot.riskLevel).text,
                  border: 'none',
                  flexShrink: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}>
                  {slot.riskLevel === 'low' ? 'Low risk' : slot.riskLevel === 'medium' ? 'Med risk' : 'High risk'}
                </span>
                <span style={{ fontSize: 13, color: C.fg, lineHeight: 1.5 }}>
                  {slot.timelineNote}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Split booking panel ──────────────────────────────────────────────────────
const SplitBookingPanel: React.FC<{
  suggestion:          RebalancingSuggestion;
  resources:           Resource[];
  acceptedId:          string | null;
  onAccept:            (id: string) => void;
  currentHoursPerWeek: number;
}> = ({ suggestion, resources, acceptedId, onAccept, currentHoursPerWeek }) => {
  const totalHours       = currentHoursPerWeek;
  const overloadPct      = Math.round(suggestion.peakUtilisation ?? 130);
  const defaultOrigPct   = Math.min(90, Math.round((100 / overloadPct) * 100));
  const [origPct, setOrigPct] = useState(defaultOrigPct);
  const secondPct        = 100 - origPct;
  const origHours        = Math.round((origPct / 100) * totalHours);
  const secondHours      = totalHours - origHours;
  const projectedUtil    = Math.round((origPct / 100) * overloadPct);

  const candidates = deriveCandidates(suggestion, resources);
  const topPick    = candidates.find((c) => c.isTopPick) ?? candidates[0];

  const utilColour = (u: number) =>
    u <= 84 ? C.teal : u <= 100 ? C.amber : C.red;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        padding: '12px 14px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
          <strong>{suggestion.atRiskResourceName}</strong> keeps a portion of this booking.
          A second person absorbs the remaining hours. No timeline slip — both work in parallel.
        </p>
      </div>

      <div style={{
        padding: '16px 18px', borderRadius: 12,
        border: '1px solid var(--border)', backgroundColor: C.card,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.fg, marginBottom: 14 }}>
          How do you want to split the hours?
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              backgroundColor: C.amber, color: '#fff',
              fontSize: 14, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 6px',
            }}>
              {(suggestion.atRiskResourceName ?? 'AT').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.fg }}>
              {suggestion.atRiskResourceName?.split(' ')[0]}
            </div>
            <div style={{ fontSize: 11, color: C.fgMuted }}>Original resource</div>
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 2,
          }}>
            <div style={{
              width: '100%', height: 10, borderRadius: 5,
              backgroundColor: C.muted, overflow: 'hidden',
              border: '1px solid var(--border)',
            }}>
              <div style={{
                height: '100%', width: `${origPct}%`,
                backgroundColor: C.amber, borderRadius: 5,
                transition: 'width 0.1s',
              }} />
            </div>
            <input
              type="range"
              min={10} max={90} step={5}
              value={origPct}
              onChange={(e) => setOrigPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: C.teal, cursor: 'pointer' }}
            />
            <div style={{ fontSize: 12, color: C.fgMuted }}>drag to adjust</div>
          </div>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 6,
              backgroundColor: 'oklch(50% .0896 194.8)', color: 'rgba(255,255,255,0.9)',
              fontSize: 14, fontWeight: 600, letterSpacing: '0.5px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 6px',
            }}>
              {topPick.initials}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.fg }}>
              {topPick.name.split(' ')[0]}
            </div>
            <div style={{ fontSize: 11, color: C.fgMuted }}>Second resource</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center',
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.amber }}>{origHours}h</div>
            <div style={{ fontSize: 11, color: C.fgMuted }}>/ week · {origPct}%</div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
          }}>
            <ArrowRight size={14} style={{ color: C.fgMuted }} />
          </div>
          <div style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center',
            border: `1px solid ${C.tealBorder}`,
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.teal }}>{secondHours}h</div>
            <div style={{ fontSize: 11, color: C.teal }}>/ week · {secondPct}%</div>
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <span style={{ fontSize: 13, color: C.fgMuted }}>
          Projected utilisation for {suggestion.atRiskResourceName?.split(' ')[0]}
        </span>
        <span style={{
          fontSize: 18, fontWeight: 800,
          color: utilColour(projectedUtil),
        }}>
          {projectedUtil}%
          {projectedUtil <= 100 && (
            <CheckCircle
              size={14}
              style={{ color: C.teal, marginLeft: 6, verticalAlign: 'middle' }}
            />
          )}
        </span>
      </div>

      <div>
        <SectionLabel>Second resource — top match</SectionLabel>
        <CandidateRow
          candidate={{ ...topPick, hoursAvailable: secondHours }}
          isTopPickRow
          onAccept={onAccept}
          acceptedId={acceptedId}
        />
      </div>
    </div>
  );
};

// ─── Reduce hours panel ───────────────────────────────────────────────────────
const ReduceHoursPanel: React.FC<{
  suggestion:          RebalancingSuggestion;
  currentHoursPerWeek: number;
  /** Lifts the chosen hours so the overlay can commit the reduction. */
  onHoursChange?:      (hours: number) => void;
}> = ({ suggestion, currentHoursPerWeek, onHoursChange }) => {
  const currentHours   = Math.max(currentHoursPerWeek, 1);
  const overloadPct    = Math.max(Math.round(suggestion.peakUtilisation ?? 130), 101);
  const minSafeHours   = Math.max(Math.floor((100 / overloadPct) * currentHours), 1);

  // Slider lower bound: 4h below the safe threshold, minimum 1h
  const sliderMin      = Math.max(minSafeHours - 4, 1);
  // Default to the safe threshold (or 8h below current if threshold > current)
  const defaultHours   = Math.min(Math.max(minSafeHours, currentHours - 8), currentHours - 1);

  const [hours, setHours] = useState(defaultHours);

  // Reset when the conflict or hours value changes (e.g. user navigates between conflicts)
  useEffect(() => {
    const next = Math.min(Math.max(Math.floor((100 / Math.max(Math.round(suggestion.peakUtilisation ?? 130), 101)) * Math.max(currentHoursPerWeek, 1)), Math.max(currentHoursPerWeek, 1) - 8), Math.max(currentHoursPerWeek, 1) - 1);
    const resetHours = Math.max(next, 1);
    setHours(resetHours);
    onHoursChange?.(resetHours);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHoursPerWeek, suggestion]);

  const projectedUtil  = currentHours > 0 ? Math.round((hours / currentHours) * overloadPct) : 0;
  const hoursReduced   = currentHours - hours;
  const weeksExtended  = hours > 0 && hoursReduced > 0 ? Math.ceil(hoursReduced / hours) : 0;

  const utilBadge  = (u: number) =>
    u <= 84 ? C.badgeTeal : u <= 100 ? C.badgeAmber : C.badgeRed;
  const riskLabel  = projectedUtil <= 84 ? 'On track' : projectedUtil <= 100 ? 'Near capacity' : 'Still overloaded';
  const riskBadge  = utilBadge(projectedUtil);

  const scopeWarning = hours <= Math.round(currentHours * 0.65);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        padding: '12px 14px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.fgMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>
          Scope negotiation required
        </div>
        <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
          Reducing hours doesn't reassign the work — it extends the timeline.
          Use this to model the conversation with the project owner before committing.
        </p>
      </div>

      <div style={{
        padding: '16px 18px', borderRadius: 12,
        border: '1px solid var(--border)', backgroundColor: C.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>
            Weekly hours on this booking
          </span>
          <span style={{ fontSize: 20, fontWeight: 800, color: C.fg }}>
            {hours}h
            <span style={{ fontSize: 13, fontWeight: 500, color: C.fgMuted }}> / week</span>
          </span>
        </div>

        <div style={{ position: 'relative', marginBottom: 8 }}>
          <input
            type="range"
            min={sliderMin} max={currentHours} step={1}
            value={hours}
            onChange={(e) => {
              const next = Number(e.target.value);
              setHours(next);
              onHoursChange?.(next);
            }}
            style={{ width: '100%', accentColor: C.teal, cursor: 'pointer' }}
          />
          {currentHours > sliderMin && (
            <div style={{
              position: 'absolute',
              left: `${Math.min(((minSafeHours - sliderMin) / (currentHours - sliderMin)) * 100, 100)}%`,
              top: 0, bottom: 0,
              width: 2,
              backgroundColor: C.teal,
              opacity: 0.5,
              pointerEvents: 'none',
            }} />
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.fgMuted }}>
          <span>{sliderMin}h min</span>
          <span style={{ color: C.teal, fontWeight: 600 }}>↑ {minSafeHours}h safe threshold</span>
          <span>{currentHours}h current</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={{
          padding: '12px', borderRadius: 10, textAlign: 'center',
          border: `1px solid ${riskBadge.bg}44`,
          backgroundColor: `${riskBadge.bg}0d`,
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: riskBadge.bg }}>{projectedUtil}%</div>
          <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 2 }}>Utilisation</div>
          <div style={{
            display: 'inline-block', marginTop: 5,
            fontSize: 11, fontWeight: 700,
            padding: '1px 7px', borderRadius: 4,
            backgroundColor: riskBadge.bg, color: riskBadge.text,
          }}>{riskLabel}</div>
        </div>
        <div style={{
          padding: '12px', borderRadius: 10, textAlign: 'center',
          border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.fg }}>
            {weeksExtended > 0 ? `+${weeksExtended}w` : '—'}
          </div>
          <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 2 }}>Timeline extension</div>
          <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 3 }}>
            {hoursReduced > 0 ? `${hoursReduced}h removed / wk` : 'No change yet'}
          </div>
        </div>
      </div>

      {scopeWarning && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', borderRadius: 10,
          border: `1px solid ${C.amber}44`,
        }}>
          <AlertTriangle size={15} style={{ color: C.amber, flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
              Significant scope reduction
            </div>
            <p style={{ margin: 0, fontSize: 13, color: C.fgMuted, lineHeight: 1.5 }}>
              Reducing to {hours}h/week is a {Math.round(100 - (hours / currentHours) * 100)}% cut.
              At this level, deliverable quality or scope must be negotiated with the project owner.
            </p>
          </div>
        </div>
      )}

      <div style={{
        padding: '12px 14px', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.fgMuted, marginBottom: 4 }}>Next step</div>
        <p style={{ margin: 0, fontSize: 13, color: C.fg, lineHeight: 1.5 }}>
          Share this projection with the project owner. If they accept a {weeksExtended}-week extension,
          apply the change to the booking. If not, consider <strong>Reassign</strong> or <strong>Split</strong> instead.
        </p>
      </div>
    </div>
  );
};

// ─── Cascade impact section ───────────────────────────────────────────────────
interface CascadeItem {
  bookingName: string;
  resourceName: string;
  delayWeeks: number;
  severity: 'low' | 'medium' | 'high';
}

const CascadeImpactBlock: React.FC<{
  items:        CascadeItem[];
  delayWeeks:   number;
}> = ({ items, delayWeeks }) => {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 14px', borderRadius: 10,
      border: '1px solid var(--border)',
    }}>
      <CheckCircle size={14} style={{ color: C.teal, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: C.fg }}>
        No downstream bookings affected by a {delayWeeks}-week delay.
      </span>
    </div>
  );

  const sevBadge = (s: CascadeItem['severity']) =>
    s === 'low' ? C.badgeTeal : s === 'medium' ? C.badgeAmber : C.badgeRed;
  const sevLabel  = (s: CascadeItem['severity']) =>
    s === 'low' ? 'Low' : s === 'medium' ? 'Med' : 'High';

  const preview  = items.slice(0, 2);
  const overflow = items.slice(2);

  return (
    <div style={{
      borderRadius: 10,
      border: '1px solid var(--border)',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
      }}>
        <Link2 size={14} style={{ color: C.amber, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: C.fg, flex: 1 }}>
          {items.length} knock-on {items.length === 1 ? 'effect' : 'effects'} from this delay
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: '2px 8px', borderRadius: 4,
          backgroundColor: C.badgeAmber.bg,
          color: C.badgeAmber.text,
          border: 'none',
        }}>
          Review required
        </span>
      </div>

      <div style={{ padding: '8px 14px 12px' }}>
        {[...preview, ...(expanded ? overflow : [])].map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0',
              borderBottom: i < preview.length - 1 || expanded
                ? '1px solid var(--border)'
                : 'none',
            }}
          >
            <div style={{
              width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
              backgroundColor: sevBadge(item.severity).bg,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>{item.bookingName}</div>
              <div style={{ fontSize: 12, color: C.fgMuted }}>{item.resourceName}</div>
            </div>
            <span style={{
              fontSize: 12, fontWeight: 700,
              padding: '2px 8px', borderRadius: 4,
              backgroundColor: sevBadge(item.severity).bg,
              color: sevBadge(item.severity).text,
              border: 'none',
              flexShrink: 0,
            }}>
              +{item.delayWeeks}w · {sevLabel(item.severity)}
            </span>
          </div>
        ))}

        {overflow.length > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 13, fontWeight: 600,
              color: C.teal, background: 'none',
              border: `1px solid ${C.tealBorder}`,
              borderRadius: 6, cursor: 'pointer', padding: '4px 10px',
              marginTop: 6,
            }}
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {expanded ? 'Show less' : `See ${overflow.length} more`}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Conflict list item ───────────────────────────────────────────────────────
const ConflictListItem: React.FC<{
  item:       ConflictItem;
  index:      number;
  isActive:   boolean;
  isResolved: boolean;
  onSelect:   (item: ConflictItem) => void;
}> = ({ item, index, isActive, isResolved, onSelect }) => (
  <button
    onClick={() => onSelect(item)}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      width: '100%', textAlign: 'left',
      padding: '10px 12px',
      borderRadius: isActive ? `0 ${VT.radius} ${VT.radius} 0` : VT.radius,
      border: isActive ? 'none' : `0.5px solid ${VT.colBorder}`,
      borderLeft: isActive
        ? `3px solid ${VT.accentTeal}`
        : isResolved
        ? `3px solid ${C.teal}`
        : `0.5px solid ${VT.colBorder}`,
      backgroundColor: isActive
        ? VT.accentTealBg
        : isResolved
        ? 'rgba(15,118,110,0.04)'
        : 'white',
      cursor: 'pointer',
      marginBottom: 5,
      transition: 'all 0.12s',
      opacity: isResolved ? 0.75 : 1,
    }}
  >
    {/* Step number or resolved check */}
    <div style={{
      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
      backgroundColor: isResolved ? C.teal : isActive ? VT.accentTeal : C.muted,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 800,
      color: isResolved || isActive ? '#fff' : C.fgMuted,
      marginTop: 1,
    }}>
      {isResolved ? <CheckCircle size={12} color="#fff" /> : index + 1}
    </div>

    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontWeight: 700, fontSize: 13,
        color: isActive ? C.teal : C.fg,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        lineHeight: 1.3, marginBottom: 3,
      }}>
        {item.projectName}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {item.dateLabel && item.dateLabel !== '—' && (
          <span style={{
            fontSize: 10, fontWeight: 600,
            padding: '1px 6px', borderRadius: 3,
            backgroundColor: isActive ? VT.accentTeal : C.muted,
            color: isActive ? '#fff' : C.fgMuted,
          }}>
            {item.dateLabel}
          </span>
        )}
        {isResolved && (
          <span style={{
            fontSize: 10, fontWeight: 600,
            padding: '1px 6px', borderRadius: 3,
            backgroundColor: C.badgeTeal.bg,
            color: C.badgeTeal.text,
          }}>
            Resolved
          </span>
        )}
      </div>
    </div>

    {isActive && !isResolved && (
      <ChevronRight size={14} style={{ color: VT.accentTealText, flexShrink: 0, marginTop: 2 }} />
    )}
  </button>
);

// ─── Suggestion list item ─────────────────────────────────────────────────────
const SuggestionListItem: React.FC<{
  suggestion: RebalancingSuggestion;
  isActive:   boolean;
  onSelect:   (s: RebalancingSuggestion) => void;
}> = ({ suggestion: s, isActive, onSelect }) => {
  const isCritical   = s.level === 'critical';
  const badge        = isCritical ? C.badgeRed : C.badgeAmber;

  return (
    <button
      onClick={() => onSelect(s)}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        padding: '11px 14px',
        borderRadius: isActive ? `0 ${VT.radius} ${VT.radius} 0` : VT.radius,
        border: isActive ? 'none' : `0.5px solid ${VT.colBorder}`,
        borderLeft: isActive ? `3px solid ${VT.accentTeal}` : `0.5px solid ${VT.colBorder}`,
        backgroundColor: isActive ? VT.accentTealBg : 'white',
        cursor: 'pointer',
        marginBottom: 6,
        transition: 'all 0.12s',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 6, marginBottom: 3,
      }}>
        <span style={{
          fontWeight: 700, fontSize: 13,
          color: isActive ? C.teal : C.fg,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {s.atRiskResourceName}
        </span>
        <span style={{
          flexShrink: 0, fontSize: 12, fontWeight: 700,
          color: badge.text, backgroundColor: badge.bg,
          borderRadius: 4, padding: '2px 7px',
          border: 'none',
        }}>
          {Math.round(s.peakUtilisation ?? 0)}%
        </span>
      </div>
      <div style={{
        fontSize: 12, color: C.fgMuted,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {s.projectName}
        {s.affectedWeekStarts.length > 0 && (
          <span style={{ fontWeight: 600, color: isActive ? C.teal : C.fgMuted }}>
            {' · '}{format(s.affectedWeekStarts[0], 'd MMM')}
          </span>
        )}
      </div>
    </button>
  );
};

// ─── Resolve all panel ────────────────────────────────────────────────────────
const ResolveAllPanel: React.FC<{
  conflictItems:    ConflictItem[];
  resources:        Resource[];
  bookings:         Booking[];
  onResolveAll:     (candidateId: string, candidateName: string) => void;
  resolvedAllWith?: string | null;
}> = ({ conflictItems, resources, bookings, onResolveAll, resolvedAllWith }) => {
  const conflictingResourceIds = useMemo(
    () => new Set(conflictItems.map((c) => c.resourceId)),
    [conflictItems],
  );

  const candidate = useMemo(() => {
    if (conflictItems.length === 0) return null;

    const conflictDates = conflictItems
      .map((c) => new Date(c.startDate))
      .filter((d) => !isNaN(d.getTime()));

    if (conflictDates.length === 0) return null;

    const minDate = new Date(Math.min(...conflictDates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...conflictDates.map((d) => d.getTime())));

    const scored = resources
      .filter(
        (r) => r.id !== 'unallocated' && !conflictingResourceIds.has(String(r.id)),
      )
      .map((r) => {
        const overlap = bookings.filter((b) => {
          if (String(b.resourceId) !== String(r.id)) return false;
          const bStart = new Date(b.startTime);
          const bEnd   = new Date(b.endTime);
          return bStart <= maxDate && bEnd >= minDate;
        });
        return { resource: r, overlapCount: overlap.length };
      })
      .sort((a, b) => a.overlapCount - b.overlapCount);

    const best = scored[0];
    if (!best) return null;

    return {
      id:      String(best.resource.id),
      name:    best.resource.name,
      role:    best.resource.role ?? 'Consultant',
      initials: best.resource.name
        .split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2),
      overlapCount: best.overlapCount,
    };
  }, [conflictItems, resources, bookings, conflictingResourceIds]);

  if (!candidate || conflictItems.length < 2) return null;

  const alreadyApplied = !!resolvedAllWith;

  return (
    <div style={{
      margin: '0 10px 4px',
      borderRadius: VT.radius,
      border: `1.5px solid ${alreadyApplied ? C.teal : VT.accentTealBorder}`,
      backgroundColor: alreadyApplied ? 'rgba(15,118,110,0.06)' : VT.accentTealBg,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
      }}>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: VT.accentTealText, lineHeight: 1.3 }}>
              {alreadyApplied ? 'Resolved all conflicts' : 'Resolve all'}
            </span>
            {!alreadyApplied && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                padding: '1px 6px', borderRadius: 3,
                background: VT.accentTeal,
                color: '#fff',
                textTransform: 'uppercase',
              }}>
                Auto
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: VT.accentTealText }}>
            {alreadyApplied
              ? `All ${conflictItems.length} reassigned to ${resolvedAllWith}`
              : `Auto-selects one resource for all ${conflictItems.length} conflicts`}
          </div>
        </div>
        {alreadyApplied && (
          <CheckCircle size={14} style={{ color: C.teal, flexShrink: 0 }} />
        )}
      </div>

      {/* Body — only when not yet applied */}
      {!alreadyApplied && (
        <div style={{
          borderTop: `1px solid ${VT.accentTealBorder}`,
          padding: '12px',
        }}>
          {/* Candidate card */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 8,
            border: `1px solid ${VT.accentTealBorder}`,
            backgroundColor: 'white',
            marginBottom: 8,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 6, flexShrink: 0,
              backgroundColor: 'oklch(50% .0896 194.8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.5px',
            }}>
              {candidate.initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: C.fg }}>
                {candidate.name}
              </div>
              <div style={{ fontSize: 11, color: C.fgMuted, marginTop: 1 }}>
                {candidate.role}
                {candidate.overlapCount === 0
                  ? ' · Fully available across all dates'
                  : ` · ${candidate.overlapCount} existing booking${candidate.overlapCount > 1 ? 's' : ''} in range`}
              </div>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 700,
              padding: '2px 8px', borderRadius: 4,
              backgroundColor: candidate.overlapCount === 0 ? C.badgeTeal.bg : C.badgeAmber.bg,
              color: '#fff',
              flexShrink: 0,
            }}>
              {candidate.overlapCount === 0 ? 'Available' : 'Partial'}
            </span>
          </div>

          <p style={{
            margin: '0 0 10px',
            fontSize: 12, color: C.fgMuted, lineHeight: 1.5,
          }}>
            Reassigns all {conflictItems.length} conflicting bookings to{' '}
            <strong style={{ color: C.fg }}>{candidate.name}</strong> in one action.
          </p>

          <button
            onClick={() => onResolveAll(candidate.id, candidate.name)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              width: '100%',
              padding: '8px 12px', borderRadius: 6,
              border: 'none',
              backgroundColor: VT.accentTeal, color: '#fff',
              fontSize: 13, fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <Users size={14} />
            Reassign all {conflictItems.length} to {candidate.name.split(' ')[0]}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Metric tile ──────────────────────────────────────────────────────────────
const MetricTile: React.FC<{ label: string; value: string; accent?: boolean }> = ({
  label, value, accent,
}) => (
  <div style={{
    flex: 1,
    padding: '10px 12px',
    borderRadius: VT.radius,
    border: `0.5px solid ${VT.colBorder}`,
    backgroundColor: VT.colBg,
    textAlign: 'center',
  }}>
    <div style={{ fontSize: 18, fontWeight: 800, color: accent ? C.teal : C.fg }}>
      {value}
    </div>
    <div style={{ fontSize: 12, fontWeight: 500, color: C.fgMuted, marginTop: 3 }}>
      {label}
    </div>
  </div>
);

// ─── Section heading helper ───────────────────────────────────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children, style,
}) => (
  <div style={{
    fontSize: 13, fontWeight: 600,
    color: C.fgMuted, marginBottom: 10,
    ...style,
  }}>
    {children}
  </div>
);

// ─── Discard confirmation dialog ──────────────────────────────────────────────
const DiscardDialog: React.FC<{
  resolvedCount: number;
  onKeep:        () => void;
  onDiscard:     () => void;
}> = ({ resolvedCount, onKeep, onDiscard }) => (
  <>
    {/* Dialog backdrop */}
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 300,
      }}
      onClick={onKeep}
      aria-hidden="true"
    />
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="discard-title"
      style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 301,
        width: 420,
        borderRadius: 14,
        backgroundColor: 'var(--card)',
        border: '1px solid var(--border)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        padding: '28px 28px 24px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3
        id="discard-title"
        style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: C.fg }}
      >
        Discard all resolutions?
      </h3>
      {resolvedCount > 0 ? (
        <p style={{ margin: '0 0 20px', fontSize: 14, color: C.fgMuted, lineHeight: 1.6 }}>
          <strong style={{ color: C.fg }}>{resolvedCount} conflict{resolvedCount !== 1 ? 's' : ''} were resolved</strong> in this session.
          Closing will discard all changes. They have not been saved to the schedule.
        </p>
      ) : (
        <p style={{ margin: '0 0 20px', fontSize: 14, color: C.fgMuted, lineHeight: 1.6 }}>
          No resolutions have been applied yet. Close without saving?
        </p>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onDiscard}
          style={{
            flex: 1, padding: '10px 16px', borderRadius: 8,
            border: `1.5px solid ${C.red}`,
            backgroundColor: 'transparent',
            color: C.red, fontSize: 14, fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Discard and close
        </button>
        <button
          onClick={onKeep}
          style={{
            flex: 1, padding: '10px 16px', borderRadius: 8,
            border: 'none',
            backgroundColor: C.teal,
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Keep resolving
        </button>
      </div>
    </div>
  </>
);

// ─── Completion state ─────────────────────────────────────────────────────────
interface ResolvedSummaryEntry {
  conflictId:   string;
  projectName:  string;
  dateLabel:    string;
  resolution:   string;
  detail:       string;
}

const CompletionScreen: React.FC<{
  resourceName:    string;
  resolvedEntries: ResolvedSummaryEntry[];
  onReturnToReport: () => void;
  onPreviewCalendar: () => void;
}> = ({ resourceName, resolvedEntries, onReturnToReport, onPreviewCalendar }) => (
  <div style={{
    flex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '40px 24px',
    overflow: 'auto',
  }}>
    {/* Tick icon */}
    <div style={{
      width: 72, height: 72, borderRadius: '50%',
      backgroundColor: VT.accentTealBg,
      border: `2px solid ${VT.accentTealBorder}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 20,
    }}>
      <CheckCircle size={38} style={{ color: VT.accentTeal }} />
    </div>

    <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: C.fg, textAlign: 'center' }}>
      All conflicts resolved
    </h2>
    <p style={{ margin: '0 0 28px', fontSize: 15, color: C.fgMuted, textAlign: 'center' }}>
      {resolvedEntries.length} conflict{resolvedEntries.length !== 1 ? 's' : ''} resolved for{' '}
      <strong style={{ color: C.fg }}>{resourceName}</strong>
    </p>

    {/* Summary list */}
    <div style={{
      width: '100%', maxWidth: 520,
      borderRadius: 12,
      border: '1px solid var(--border)',
      overflow: 'hidden',
      marginBottom: 28,
    }}>
      {resolvedEntries.map((entry, i) => (
        <div
          key={entry.conflictId}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px',
            borderBottom: i < resolvedEntries.length - 1 ? '1px solid var(--border)' : 'none',
            backgroundColor: 'var(--card)',
          }}
        >
          <CheckCircle size={16} style={{ color: VT.accentTeal, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.fg }}>
              {entry.projectName}
            </div>
            <div style={{ fontSize: 12, color: C.fgMuted, marginTop: 2 }}>
              {entry.resolution}
              {entry.detail ? ` · ${entry.detail}` : ''}
              {entry.dateLabel ? ` · ${entry.dateLabel}` : ''}
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Action buttons */}
    <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 520 }}>
      <button
        onClick={onReturnToReport}
        style={{
          flex: 1, padding: '12px 20px', borderRadius: 8,
          border: 'none',
          backgroundColor: VT.accentTeal, color: '#fff',
          fontSize: 15, fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Return to report
      </button>
      <button
        onClick={onPreviewCalendar}
        style={{
          flex: 1, padding: '12px 20px', borderRadius: 8,
          border: '1.5px solid var(--border)',
          backgroundColor: 'transparent', color: C.fg,
          fontSize: 15, fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Preview all changes in calendar
      </button>
    </div>
  </div>
);

// ─── Resolve-all review state (centre + right columns) ────────────────────────
interface ResolveAllReviewRow {
  index:      number;
  item:       ConflictItem;
  candidateName: string;
  accepted:   boolean;
}

const ResolveAllReviewPanel: React.FC<{
  rows:            ResolveAllReviewRow[];
  candidateName:   string;
  onAcceptRow:     (index: number) => void;
  onEditRow:       (index: number) => void;
  onConfirmAll:    () => void;
  busy?:           boolean;
}> = ({ rows, candidateName, onAcceptRow, onEditRow, onConfirmAll, busy = false }) => {
  const allAccepted = rows.every((r) => r.accepted);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header strip */}
      <div style={{
        padding: '16px 24px 12px',
        borderBottom: `0.5px solid ${VT.colBorder}`,
        backgroundColor: VT.colBgPrimary,
        flexShrink: 0,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
          Reassigning all {rows.length} conflicts to {candidateName} — review below
        </div>
        <div style={{ fontSize: 13, color: C.fgMuted }}>
          Accept each reassignment or Edit to resolve individually
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((row) => (
            <div
              key={row.index}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', borderRadius: VT.radius,
                border: row.accepted
                  ? `1.5px solid ${C.teal}`
                  : `0.5px solid ${VT.colBorder}`,
                backgroundColor: row.accepted ? 'rgba(15,118,110,0.04)' : VT.colBgPrimary,
              }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                backgroundColor: row.accepted ? C.teal : C.muted,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
                color: row.accepted ? '#fff' : C.fgMuted,
              }}>
                {row.accepted ? <CheckCircle size={13} color="#fff" /> : row.index + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.fg }}>
                  {row.item.projectName}
                </div>
                <div style={{ fontSize: 12, color: C.fgMuted, marginTop: 2 }}>
                  Reassign to {candidateName}
                  {row.item.dateLabel ? ` · ${row.item.dateLabel}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {!row.accepted && (
                  <button
                    onClick={() => onAcceptRow(row.index)}
                    style={{
                      fontSize: 12, fontWeight: 700,
                      padding: '5px 12px', borderRadius: 6,
                      border: 'none',
                      backgroundColor: C.teal, color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Accept
                  </button>
                )}
                {row.accepted && (
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    padding: '5px 12px', borderRadius: 6,
                    backgroundColor: C.badgeTeal.bg, color: C.badgeTeal.text,
                  }}>
                    ✓ Accepted
                  </span>
                )}
                <button
                  onClick={() => onEditRow(row.index)}
                  style={{
                    fontSize: 12, fontWeight: 600,
                    padding: '5px 12px', borderRadius: 6,
                    border: `1px solid ${VT.colBorder}`,
                    backgroundColor: 'transparent', color: C.fgMuted,
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Confirm all button */}
      <div style={{
        padding: '14px 24px',
        borderTop: `0.5px solid ${VT.colBorder}`,
        backgroundColor: VT.colBgPrimary,
        flexShrink: 0,
        display: 'flex', justifyContent: 'center',
      }}>
        <button
          onClick={onConfirmAll}
          disabled={busy}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: 'auto', padding: '11px 32px', borderRadius: 8,
            border: 'none',
            backgroundColor: allAccepted ? VT.accentTeal : C.teal,
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          <CheckCircle size={16} />
          {busy ? 'Applying changes…' : 'Confirm all reassignments'}
        </button>
      </div>
    </div>
  );
};

// ─── Public props ──────────────────────────────────────────────────────────────
export interface WhatIfConflictResolutionOverlayProps {
  open:               boolean;
  suggestions:        RebalancingSuggestion[];
  suggestion:         RebalancingSuggestion | null;
  onSelectSuggestion: (s: RebalancingSuggestion) => void;
  risks:              BurnoutRisk[];
  resources:          Resource[];
  bookings:           Booking[];
  onClose:            () => void;
  /** Persists the plan. When it returns a promise the overlay waits for it to
   *  settle before showing the completion screen, so "Preview all changes in
   *  calendar" can never hand off to the planner on pre-commit data. */
  onApply:            (s: RebalancingSuggestion) => void | Promise<void>;
  /** Completion screen "Preview all changes in calendar" action. Falls back to
   *  `onClose` when the host has no calendar to hand off to. */
  onPreviewCalendar?: () => void;
  conflictItems?:     ConflictItem[];
  /** Working-hours profile used by the capacity-aware distribution so the
   *  overlay's displayed target resource(s) match the calendar preview. */
  globalWorkingProfile?: WorkingHoursProfile;
}

// ─── Main overlay ─────────────────────────────────────────────────────────────
export const WhatIfConflictResolutionOverlay: React.FC<WhatIfConflictResolutionOverlayProps> = ({
  open,
  suggestions,
  suggestion,
  onSelectSuggestion,
  risks,
  resources,
  bookings,
  onClose,
  onApply,
  onPreviewCalendar,
  conflictItems: externalConflictItems,
  globalWorkingProfile,
}) => {
  const { isEnabled }                               = useConflictResolutionSettings();
  const enabledOptions                              = RESOLUTION_OPTIONS.filter((o) => isEnabled(o.id));
  const [selectedResolution, setSelectedResolution] = useState<ResolutionType>('reassign');
  const [sortOrder, setSortOrder]                   = useState<'severity' | 'date'>('severity');
  const [acceptedCandidateId, setAcceptedCandidateId] = useState<string | null>(null);
  const [selectedDelaySlot, setSelectedDelaySlot]   = useState<string | null>('slot-1');

  // Conflict queue mode state
  const hasConflictQueue                            = (externalConflictItems?.length ?? 0) > 0;
  const [activeConflictIdx, setActiveConflictIdx]   = useState(0);
  const [resolvedIndices, setResolvedIndices]       = useState<Set<number>>(new Set());

  // Track what resolution was used for each conflict (for completion summary)
  const [resolvedEntries, setResolvedEntries]       = useState<ResolvedSummaryEntry[]>([]);

  // Completion state
  const [showCompletion, setShowCompletion]         = useState(false);

  // True while the accumulated plan is being persisted by the host.
  const [isApplying, setIsApplying]                 = useState(false);

  // Discard confirmation dialog
  const [showDiscardDialog, setShowDiscardDialog]   = useState(false);

  // Resolve-all review state
  // null = not in resolve-all mode
  // populated = showing review rows
  const [resolveAllCandidateName, setResolveAllCandidateName] = useState<string | null>(null);
  const [resolveAllCandidateId, setResolveAllCandidateId] = useState<string | null>(null);
  const [resolveAllReviewRows, setResolveAllReviewRows] = useState<ResolveAllReviewRow[]>([]);
  const isInResolveAllReview = resolveAllCandidateName !== null && resolveAllReviewRows.length > 0 && !showCompletion;

  // Reassignments accumulated as conflicts are individually resolved in queue
  // mode. Committed to the parent via `onApply` when the process completes so
  // the newly-chosen resource is actually persisted, not just shown as resolved.
  const [pendingAssignments, setPendingAssignments] = useState<
    Array<{ bookingId: string; candidateResourceId: string; candidateResourceName: string }>
  >([]);

  // Hour reductions / start delays accumulated the same way, so "Reduce hours"
  // and "Delay start" commit real booking changes instead of only being modelled.
  const [pendingReductions, setPendingReductions] = useState<
    Array<{ bookingId: string; newWeeklyHours: number }>
  >([]);
  const [pendingDelays, setPendingDelays] = useState<
    Array<{ bookingId: string; newStartDate: string }>
  >([]);

  // Hours currently chosen in the Reduce panel (lifted so the overlay can commit it)
  const [reducedHours, setReducedHours] = useState<number | null>(null);

  // Derived: are we in "edit one conflict from resolve-all" mode?
  const [editingFromResolveAll, setEditingFromResolveAll] = useState(false);

  const activeConflictItem = hasConflictQueue
    ? (externalConflictItems![activeConflictIdx] ?? externalConflictItems![0])
    : null;

  // Reset per-conflict UI state when active conflict changes
  const resetConflictUI = useCallback(() => {
    setAcceptedCandidateId(null);
    setSelectedDelaySlot('slot-1');
    setReducedHours(null);
    const firstEnabled = RESOLUTION_OPTIONS.find((o) => isEnabled(o.id));
    setSelectedResolution(firstEnabled?.id ?? 'reassign');
  }, [isEnabled]);

  // Derived from live bookings
  const currentHoursPerWeek = useMemo(
    () => suggestion
      ? deriveWeeklyHours(
          suggestion.atRiskResourceId,
          suggestion.projectName,
          suggestion.affectedWeekStarts,
          bookings,
        )
      : 40,
    [suggestion, bookings],
  );

  const weeklyCapacity = 40;

  // Bookings a reduce/delay actually changes: the at-risk resource's bookings on
  // this project in the affected weeks — the same set `deriveWeeklyHours`
  // measures, so the panel's "Xh / week" and the resulting mutation agree.
  // `atRiskBookingIds` is deliberately NOT used here: it holds the *surplus*
  // bookings picked to move, which is the reassign plan, not what is reduced.
  const inPlaceTargetBookingIds = useMemo(() => {
    if (!suggestion) return [];
    const weeks = suggestion.affectedWeekStarts ?? [];
    const ids = bookings
      .filter((b) => {
        if (String(b.resourceId) !== String(suggestion.atRiskResourceId)) return false;
        if (b.projectName !== suggestion.projectName && b.projectId !== suggestion.projectName) return false;
        if (b.status === 'cancelled') return false;
        if (weeks.length === 0) return true;
        const start = new Date(b.startTime);
        const end = new Date(b.endTime);
        return weeks.some((ws) => start < endOfWeek(ws, { weekStartsOn: 1 }) && end > ws);
      })
      .map((b) => String(b.id));
    return ids.length > 0 ? ids : (suggestion.atRiskBookingIds ?? []).map(String);
  }, [suggestion, bookings]);

  // Week the delay is measured from. Shared by the availability scan, the
  // callout and the Delay panel so every "+N weeks" is the same arithmetic.
  const delayBaseDate = useMemo(
    () => suggestion?.affectedWeekStarts[0] ?? startOfWeek(new Date(), { weekStartsOn: 1 }),
    [suggestion],
  );

  const nextAvailableWeeks = useMemo(
    () => suggestion
      ? findNextAvailableWeeks(
          suggestion.atRiskResourceId,
          delayBaseDate,
          bookings,
          weeklyCapacity,
          80,
          3,
          1,
          inPlaceTargetBookingIds,
        )
      : [],
    [suggestion, delayBaseDate, bookings, weeklyCapacity, inPlaceTargetBookingIds],
  );

  // The slot the "Recommendation" callout describes — the same first entry the
  // Delay panel renders as its top card, so the two can never disagree.
  const recommendedDelay = useMemo(() => {
    const week = nextAvailableWeeks[0];
    if (!week) return null;
    return { ...week, delayWeeks: delayWeeksBetween(delayBaseDate, week.weekStart) };
  }, [nextAvailableWeeks, delayBaseDate]);

  // ── Reduce / Delay outcomes for a set of bookings ─────────────────────────
  // The panels model the change at PROJECT level (hours per week, target week).
  // These map that choice onto each concrete booking so it can be persisted:
  //   • reduce → scale the booking's own hours by the chosen ratio
  //   • delay  → the chosen week's slot, keeping the booking's weekday offset
  const buildReductions = useCallback(
    (bookingIds: string[]): Array<{ bookingId: string; newWeeklyHours: number }> => {
      if (reducedHours == null || currentHoursPerWeek <= 0) return [];
      const ratio = reducedHours / currentHoursPerWeek;
      if (!(ratio > 0) || ratio >= 1) return [];
      return bookingIds.flatMap((id) => {
        const booking = bookings.find((b) => String(b.id) === String(id));
        if (!booking) return [];
        const hours = bookingTotalHours(booking) * ratio;
        if (!(hours > 0)) return [];
        return [{ bookingId: String(id), newWeeklyHours: Math.round(hours * 100) / 100 }];
      });
    },
    [reducedHours, currentHoursPerWeek, bookings],
  );

  const buildDelays = useCallback(
    (bookingIds: string[]): Array<{ bookingId: string; newStartDate: string }> => {
      if (!selectedDelaySlot) return [];
      const slotIdx = Number(selectedDelaySlot.replace('slot-', '')) - 1;
      const slotDate = nextAvailableWeeks[slotIdx]?.weekStart;
      if (!slotDate) return [];
      return bookingIds.flatMap((id) => {
        const booking = bookings.find((b) => String(b.id) === String(id));
        if (!booking) return [];
        const start = new Date(booking.startTime);
        if (Number.isNaN(start.getTime())) return [];
        // Keep the booking on the same weekday within the chosen week.
        const weekdayOffset = differenceInCalendarDays(
          start,
          startOfWeek(start, { weekStartsOn: 1 }),
        );
        const newStart = new Date(slotDate);
        newStart.setDate(newStart.getDate() + weekdayOffset);
        if (differenceInCalendarDays(newStart, start) === 0) return [];
        return [{ bookingId: String(id), newStartDate: newStart.toISOString() }];
      });
    },
    [selectedDelaySlot, nextAvailableWeeks, bookings],
  );

  // Handler: mark current conflict resolved and advance
  const handleMarkResolved = useCallback(async () => {
    if (!externalConflictItems || !suggestion || isApplying) return;

    // Build the summary entry for this conflict
    const currentItem = externalConflictItems[activeConflictIdx];
    const resLabel = RESOLUTION_OPTIONS.find((o) => o.id === selectedResolution)?.label ?? selectedResolution;
    let detail = '';
    if (selectedResolution === 'reassign' && acceptedCandidateId) {
      const r = resources.find((x) => String(x.id) === acceptedCandidateId);
      detail = r ? `Assigned to ${r.name}` : '';
    } else if (selectedResolution === 'delay' && selectedDelaySlot) {
      const slotIdx = Number(selectedDelaySlot.replace('slot-', '')) - 1;
      const slotDate = nextAvailableWeeks[slotIdx]?.weekStart;
      detail = slotDate ? `Delayed to w/c ${format(slotDate, 'd MMM')}` : 'Date delayed';
    } else if (selectedResolution === 'reduce') {
      detail = reducedHours != null ? `Hours reduced to ${reducedHours}h/week` : 'Hours reduced';
    }

    const entry: ResolvedSummaryEntry = {
      conflictId:  currentItem?.id ?? String(activeConflictIdx),
      projectName: currentItem?.projectName ?? '',
      dateLabel:   currentItem?.dateLabel ?? '',
      resolution:  resLabel,
      detail,
    };

    setResolvedEntries((prev) => [...prev, entry]);

    setResolvedIndices((prev) => {
      const next = new Set(prev);
      next.add(activeConflictIdx);
      return next;
    });

    // Record the reassignment so it can be committed on completion. The
    // conflict queue is keyed 1:1 with `atRiskBookingIds`, so the active
    // index maps directly to the booking that must move.
    const currentBookingId = suggestion.atRiskBookingIds?.[activeConflictIdx];
    let nextAssignments = pendingAssignments;
    let nextReductions = pendingReductions;
    let nextDelays = pendingDelays;
    // Reduce/delay act on the project bookings the panel measured, not on the
    // surplus booking the reassign plan would have moved.
    if (selectedResolution === 'reduce') {
      const reductions = buildReductions(inPlaceTargetBookingIds);
      if (reductions.length > 0) {
        const changed = new Set(reductions.map((r) => r.bookingId));
        nextReductions = [
          ...pendingReductions.filter((r) => !changed.has(r.bookingId)),
          ...reductions,
        ];
        setPendingReductions(nextReductions);
      }
    } else if (selectedResolution === 'delay') {
      const delays = buildDelays(inPlaceTargetBookingIds);
      if (delays.length > 0) {
        const changed = new Set(delays.map((d) => d.bookingId));
        nextDelays = [
          ...pendingDelays.filter((d) => !changed.has(d.bookingId)),
          ...delays,
        ];
        setPendingDelays(nextDelays);
      }
    }
    if (
      selectedResolution === 'reassign' &&
      acceptedCandidateId &&
      currentBookingId &&
      String(acceptedCandidateId) !== String(suggestion.atRiskResourceId)
    ) {
      const target = resources.find((x) => String(x.id) === String(acceptedCandidateId));
      nextAssignments = [
        ...pendingAssignments.filter((a) => a.bookingId !== String(currentBookingId)),
        {
          bookingId:             String(currentBookingId),
          candidateResourceId:   String(acceptedCandidateId),
          candidateResourceName: target?.name ?? '',
        },
      ];
      setPendingAssignments(nextAssignments);
    }

    // Build the updated resolved set immediately (don't rely on stale state)
    const updatedResolved = new Set(resolvedIndices);
    updatedResolved.add(activeConflictIdx);
    const total = externalConflictItems.length;

    if (updatedResolved.size >= total) {
      // Last conflict — persist every accumulated change, then complete. The
      // commit is awaited so the completion screen (and its hand-off to the
      // planner) only appears once the bookings have actually moved.
      if (nextAssignments.length > 0 || nextReductions.length > 0 || nextDelays.length > 0) {
        setIsApplying(true);
        try {
          await onApply({
            ...suggestion,
            plannedAssignments: nextAssignments.length > 0 ? nextAssignments : undefined,
            plannedReductions:  nextReductions.length  > 0 ? nextReductions  : undefined,
            plannedDelays:      nextDelays.length      > 0 ? nextDelays      : undefined,
          });
        } finally {
          setIsApplying(false);
        }
      }
      setShowCompletion(true);
    } else {
      // If returning from resolve-all edit mode, go back to review
      if (editingFromResolveAll) {
        setEditingFromResolveAll(false);
      } else {
        // Advance to next unresolved using the freshly-updated set
        const nextIdx = externalConflictItems.findIndex(
          (_, i) => i > activeConflictIdx && !updatedResolved.has(i),
        );
        if (nextIdx !== -1) {
          setActiveConflictIdx(nextIdx);
          resetConflictUI();
        }
      }
    }
  }, [
    activeConflictIdx,
    externalConflictItems,
    resolvedIndices,
    selectedResolution,
    acceptedCandidateId,
    selectedDelaySlot,
    resources,
    editingFromResolveAll,
    resetConflictUI,
    suggestion,
    pendingAssignments,
    pendingReductions,
    pendingDelays,
    buildReductions,
    buildDelays,
    inPlaceTargetBookingIds,
    reducedHours,
    nextAvailableWeeks,
    onApply,
    isApplying,
  ]);

  // Handler: initiate resolve-all review
  const handleResolveAll = useCallback((candidateId: string, candidateName: string) => {
    if (!externalConflictItems) return;
    setResolveAllCandidateName(candidateName);
    setResolveAllCandidateId(candidateId);
    setResolveAllReviewRows(
      externalConflictItems.map((item, index) => ({
        index,
        item,
        candidateName,
        // Pre-accept any conflicts that were already resolved individually
        accepted: resolvedIndices.has(index),
      })),
    );
  }, [externalConflictItems, resolvedIndices]);

  // Handler: accept individual row in resolve-all review
  const handleAcceptResolveAllRow = useCallback((index: number) => {
    setResolveAllReviewRows((prev) =>
      prev.map((r) => r.index === index ? { ...r, accepted: true } : r),
    );
  }, []);

  // Handler: edit individual row from resolve-all review
  const handleEditResolveAllRow = useCallback((index: number) => {
    setActiveConflictIdx(index);
    resetConflictUI();
    setEditingFromResolveAll(true);
    setResolveAllCandidateName(null); // exit review mode temporarily
    setResolveAllReviewRows([]);
  }, [resetConflictUI]);

  // Handler: confirm all reassignments in resolve-all review
  const handleConfirmResolveAll = useCallback(async () => {
    if (!externalConflictItems || !resolveAllCandidateName || !resolveAllCandidateId || !suggestion) return;
    if (isApplying) return;

    // Build summary entries for all conflicts
    const entries: ResolvedSummaryEntry[] = externalConflictItems.map((item) => ({
      conflictId:  item.id,
      projectName: item.projectName,
      dateLabel:   item.dateLabel,
      resolution:  'Reassign to team member',
      detail:      `Assigned to ${resolveAllCandidateName}`,
    }));

    // Persist the reassignment: every surplus booking moves to the single
    // chosen candidate. The queue is keyed 1:1 with `atRiskBookingIds`.
    const bookingIds = suggestion.atRiskBookingIds ?? [];
    const plannedAssignments = bookingIds.map((bookingId) => ({
      bookingId:             String(bookingId),
      candidateResourceId:   String(resolveAllCandidateId),
      candidateResourceName: resolveAllCandidateName,
    }));
    if (plannedAssignments.length > 0 || pendingReductions.length > 0 || pendingDelays.length > 0) {
      setIsApplying(true);
      try {
        await onApply({
          ...suggestion,
          candidateId:   String(resolveAllCandidateId),
          candidateName: resolveAllCandidateName,
          plannedAssignments: plannedAssignments.length > 0 ? plannedAssignments : undefined,
          // Reduce/delay outcomes chosen for individual conflicts before the
          // resolve-all sweep still need committing.
          plannedReductions: pendingReductions.length > 0 ? pendingReductions : undefined,
          plannedDelays:     pendingDelays.length     > 0 ? pendingDelays     : undefined,
        });
      } finally {
        setIsApplying(false);
      }
    }

    setResolvedEntries(entries);
    setResolvedIndices(new Set(externalConflictItems.map((_, i) => i)));
    setShowCompletion(true);
  }, [
    externalConflictItems,
    resolveAllCandidateName,
    resolveAllCandidateId,
    suggestion,
    pendingReductions,
    pendingDelays,
    onApply,
    isApplying,
  ]);

  // Discard: called from close button or close action
  const handleRequestClose = useCallback(() => {
    if (resolvedIndices.size > 0) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }, [resolvedIndices.size, onClose]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardDialog(false);
    onClose();
  }, [onClose]);

  const handleKeepResolving = useCallback(() => {
    setShowDiscardDialog(false);
  }, []);

  // If the selected option was disabled in Settings, fall back to the first enabled one
  useEffect(() => {
    if (!isEnabled(selectedResolution) && enabledOptions.length > 0) {
      setSelectedResolution(enabledOptions[0].id);
    }
  }, [isEnabled, selectedResolution, enabledOptions]);

  useEffect(() => {
    if (open) {
      const firstEnabled = RESOLUTION_OPTIONS.find((o) => isEnabled(o.id));
      setSelectedResolution(firstEnabled?.id ?? 'reassign');
      setAcceptedCandidateId(null);
      setSelectedDelaySlot('slot-1');
      setActiveConflictIdx(0);
      setResolvedIndices(new Set());
      setResolvedEntries([]);
      setShowCompletion(false);
      setShowDiscardDialog(false);
      setResolveAllCandidateName(null);
      setResolveAllCandidateId(null);
      setResolveAllReviewRows([]);
      setPendingAssignments([]);
      setPendingReductions([]);
      setPendingDelays([]);
      setReducedHours(null);
      setEditingFromResolveAll(false);
      setIsApplying(false);
    }
  }, [open, suggestion?.atRiskResourceId]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // ── Capacity-aware distribution — the SAME plan the calendar preview applies.
  // Placing ALL surplus on one free resource when possible, else spreading it.
  // The overlay displays exactly these destination resource(s) and threads the
  // plan through `plannedAssignments` so "Preview in calendar" stays in sync.
  const distribution: SurplusDistributionResult = useMemo(() => {
    if (!suggestion) {
      return { mode: 'none', assignments: [], targetResourceIds: [], blockedBookingIds: [], excludedResourceIds: [] };
    }
    const atRiskResource =
      resources.find((r) => String(r.id) === String(suggestion.atRiskResourceId)) ?? null;
    const surplus = deriveSurplusBookings(
      suggestion,
      bookings,
      atRiskResource,
      globalWorkingProfile ?? DEFAULT_WORKING_PROFILE,
    );
    return distributeSurplusBookings({
      surplus,
      resources,
      existingBookings: bookings,
      globalProfile: globalWorkingProfile ?? DEFAULT_WORKING_PROFILE,
      atRiskResourceId: suggestion.atRiskResourceId,
      atRiskResource,
      preferredResourceId: acceptedCandidateId ?? suggestion.candidateId,
      // Never redistribute onto another already-at-risk resource — it would
      // just re-trigger their own burnout alert.
      excludeResourceIds: risks
        .map((r) => String(r.resourceId))
        .filter((id) => id !== String(suggestion.atRiskResourceId)),
    });
  }, [suggestion, bookings, resources, globalWorkingProfile, acceptedCandidateId, risks]);

  if (!open || !suggestion) return null;

  const displayResourceName  = activeConflictItem?.resourceName  ?? suggestion.atRiskResourceName;
  const displayProjectName   = activeConflictItem?.projectName   ?? suggestion.projectName;
  const displayDateLabel     = activeConflictItem?.dateLabel      ?? '';

  const risk       = risks.find((r) => String(r.resourceId) === String(suggestion.atRiskResourceId));
  const peakUtil   = risk?.peakUtilisation ?? suggestion.peakUtilisation ?? 112;
  const activeOption = RESOLUTION_OPTIONS.find((o) => o.id === selectedResolution) ?? RESOLUTION_OPTIONS[0];

  const sortedSuggestions = [...suggestions].sort((a, b) =>
    sortOrder === 'severity'
      ? (b.peakUtilisation ?? 0) - (a.peakUtilisation ?? 0)
      : (a.affectedWeekStarts[0]?.getTime() ?? 0) - (b.affectedWeekStarts[0]?.getTime() ?? 0),
  );

  const handleAccept = (id: string) =>
    setAcceptedCandidateId((prev) => (prev === id ? null : id));

  const totalConflicts = externalConflictItems?.length ?? 0;
  const resolvedCount  = resolvedIndices.size;
  const progressPct    = totalConflicts > 0 ? (resolvedCount / totalConflicts) * 100 : 0;
  const isFinalConflict = hasConflictQueue && (resolvedCount + 1 >= totalConflicts);

  // Derive apply button label per spec §11
  const applyButtonLabel = (() => {
    if (!hasConflictQueue) return 'Preview in calendar';
    if (isFinalConflict) return 'Apply & finish';
    if (selectedResolution === 'reduce') return 'Apply hour reduction & next →';
    if (selectedResolution === 'delay') return 'Apply delay & next →';
    return 'Apply & resolve next →';
  })();

  // Right column flex weight per spec §12
  const rightColFlex = 1;

  const distributionTargets = distribution.targetResourceIds.map((id) => ({
    id,
    name: resources.find((r) => String(r.id) === String(id))?.name ?? id,
  }));
  const distributionSpread = distributionTargets.length > 1;
  // The fine-grained per-day distribution is the SINGLE SOURCE OF TRUTH for who
  // can actually take work. When it places nothing, there is no resource the
  // overlay can honestly recommend — regardless of what the coarse weekly
  // `suggestion.candidateId` (Top Pick) says — so we must present the
  // "no suitable resource / manual rebalancing" state instead of a Top Pick
  // with an Assign button that the plan would then block.
  const distributionPlacedNothing = distribution.assignments.length === 0;
  // Names of resources that were intentionally excluded as targets (already at
  // burnout risk). Surfaced so a "no suitable resource" outcome is explainable
  // rather than mysterious.
  const excludedAtRiskNames = distribution.excludedResourceIds
    .map((id) => resources.find((r) => String(r.id) === String(id))?.name)
    .filter((n): n is string => !!n);
  // Destination label shown in the recommendation — the single absorber, or a
  // "N resources (a, b, …)" summary when the surplus had to be spread.
  const distributionTargetLabel = distributionTargets.length === 0
    ? null
    : distributionSpread
      ? `${distributionTargets.length} resources (${distributionTargets.map((t) => t.name).join(', ')})`
      : distributionTargets[0].name;

  // The resource the preview must actually reassign to. This is derived purely
  // from the distribution result so the recommendation, the redistribution
  // rows, the calendar preview ghosts and the commit all name the SAME
  // resource(s):
  //   • nothing placed        → no candidate (manual rebalancing state)
  //   • single absorber        → that resource
  //   • spread across many      → no single candidate (per-booking plan shown),
  //                               unless the user explicitly accepted one of the
  //                               actual distribution targets.
  // The effective candidate can NEVER be the at-risk resource itself — moving
  // work back onto the over-loaded person would be a no-op.
  const isAtRiskId = (id: string | null | undefined) =>
    id != null && String(id) === String(suggestion.atRiskResourceId);

  const resolveEffectiveCandidate = (): { id: string; name: string; role: string } | null => {
    if (distributionPlacedNothing) return null;

    const targetOf = (id: string) => {
      const res = resources.find((r) => String(r.id) === String(id));
      return {
        id: String(id),
        name: res?.name ?? distributionTargets.find((t) => String(t.id) === String(id))?.name ?? String(id),
        role: res?.role ?? suggestion.candidateRole ?? '',
      };
    };

    // A single absorber is the recommendation.
    if (!distributionSpread && distributionTargets.length === 1 && !isAtRiskId(distributionTargets[0].id)) {
      return targetOf(distributionTargets[0].id);
    }

    // Spread across several resources: only name one when the user explicitly
    // accepted a resource that the distribution actually placed work on.
    if (
      acceptedCandidateId &&
      !isAtRiskId(acceptedCandidateId) &&
      distributionTargets.some((t) => String(t.id) === String(acceptedCandidateId))
    ) {
      return targetOf(acceptedCandidateId);
    }

    return null;
  };

  const buildAppliedSuggestion = (): RebalancingSuggestion => {
    const effective = resolveEffectiveCandidate();
    // Reduce / delay change the booking in place — they never reassign it.
    if (selectedResolution === 'reduce' || selectedResolution === 'delay') {
      const targetBookingIds =
        inPlaceTargetBookingIds.length > 0
          ? inPlaceTargetBookingIds
          : distribution.assignments.map((a) => String(a.bookingId));
      const plannedReductions =
        selectedResolution === 'reduce' ? buildReductions(targetBookingIds) : [];
      const plannedDelays =
        selectedResolution === 'delay' ? buildDelays(targetBookingIds) : [];
      if (plannedReductions.length === 0 && plannedDelays.length === 0) return suggestion;
      return {
        ...suggestion,
        plannedReductions: plannedReductions.length > 0 ? plannedReductions : undefined,
        plannedDelays:     plannedDelays.length     > 0 ? plannedDelays     : undefined,
      };
    }
    const plannedAssignments =
      distribution.assignments.length > 0 ? distribution.assignments : undefined;
    // When the surplus is spread across several resources, surface a summary
    // name and the primary target id so the banner/preview read correctly.
    const primaryId = distributionTargets[0]?.id ?? effective?.id;
    const spreadName = distributionSpread
      ? `${distributionTargets.length} resources`
      : undefined;

    if (!effective && !plannedAssignments) return suggestion;

    // Guard: never let the applied candidate id resolve to the at-risk resource.
    const safePrimaryId = isAtRiskId(primaryId) ? effective?.id : primaryId;

    return {
      ...suggestion,
      candidateId: safePrimaryId ?? suggestion.candidateId,
      candidateName: spreadName ?? effective?.name ?? suggestion.candidateName,
      candidateRole: effective?.role ?? suggestion.candidateRole,
      plannedAssignments,
    };
  };

  // Single source of truth for the candidate shown across all three surfaces
  // (header subtitle, right-column top pick, applied scenario). Only surface a
  // single named candidate when the plan isn't a multi-resource spread (unless
  // the user explicitly accepted one).
  const effectiveCandidate = resolveEffectiveCandidate();
  const spreadWithoutAccept = distributionSpread && !acceptedCandidateId;
  const effectiveCandidateId =
    effectiveCandidate && !spreadWithoutAccept ? effectiveCandidate.id : null;
  const displayCandidateLabel = effectiveCandidateId
    ? effectiveCandidate!.name
    : distributionSpread
      ? distributionTargetLabel
      : effectiveCandidate?.name ?? null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 200,
        }}
        onClick={hasConflictQueue ? handleRequestClose : onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="What-if Analysis: Burnout Resolution"
        data-tour="whatif-overlay"
        className="fixed inset-6 z-[201] flex flex-col rounded-xl overflow-hidden border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ══ HEADER ══════════════════════════════════════════════════════ */}
        <div className="flex flex-col shrink-0 border-b bg-muted/50">
          {/* Row 1: icon · title · subtitle · badge · close button */}
          <div className="flex items-center gap-3 px-6 py-4">
            {/* Icon container — matches Suggested Project Plan style */}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
              <GitMerge className="h-4 w-4 text-primary" />
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold leading-none text-foreground">
                What-if Analysis: Burnout Resolution
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                <span>
                  {displayResourceName} · {displayProjectName}
                  {displayCandidateLabel ? ` → ${displayCandidateLabel}` : ''}
                  {displayDateLabel
                    ? ` · ${displayDateLabel}`
                    : suggestion.affectedWeekStarts.length > 0
                    ? ` · from ${format(suggestion.affectedWeekStarts[0], 'd MMM yyyy')}`
                    : ''}
                </span>
                {hasConflictQueue && totalConflicts > 1 && (
                  <Badge
                    variant="accent"
                    accent={showCompletion ? 'teal' : 'warning'}
                    className="text-[10px] shrink-0"
                  >
                    {showCompletion ? 'All resolved' : `${resolvedCount + 1} of ${totalConflicts}`}
                  </Badge>
                )}
              </p>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={hasConflictQueue ? handleRequestClose : onClose}
              aria-label="Close overlay"
              className="shrink-0 h-8 w-8"
            >
              <X size={16} />
            </Button>
          </div>

          {/* Row 2: progress bar + labels — only in conflict queue mode */}
          {hasConflictQueue && totalConflicts > 1 && !showCompletion && (
            <div className="px-6 pb-3">
              {/* Progress bar */}
              <div className="h-1 rounded-full bg-border overflow-hidden mb-1.5">
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${progressPct}%`,
                    backgroundColor: VT.accentTeal,
                  }}
                />
              </div>
              {/* Labels below bar */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {activeConflictItem?.projectName ?? displayProjectName}
                  {displayDateLabel ? ` · ${displayDateLabel}` : ''}
                </span>
                <span className="text-xs font-semibold text-muted-foreground">
                  {totalConflicts - resolvedCount} remaining
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ══ BODY ════════════════════════════════════════════════════════ */}

        {/* Completion state replaces 3-column layout */}
        {showCompletion ? (
          <CompletionScreen
            resourceName={displayResourceName}
            resolvedEntries={resolvedEntries}
            onReturnToReport={onClose}
            onPreviewCalendar={onPreviewCalendar ?? onClose}
          />
        ) : (
          <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

            {/* ── RAIL — conflict / at-risk list ──────────────────────── */}
            <div style={{
              width: '18%', flexShrink: 0,
              backgroundColor: 'var(--muted)',
              borderRight: `0.5px solid ${VT.colBorder}`,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              {/* Rail header */}
              <div style={{
                padding: '14px 14px 10px',
                borderBottom: `0.5px solid ${VT.colBorder}`,
                backgroundColor: 'var(--card)',
                flexShrink: 0,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', gap: 6,
                  marginBottom: 8,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.fg }}>
                    {hasConflictQueue ? 'Conflicts' : 'At-risk resources'}
                  </span>
                  <span style={{
                    fontSize: 12, color: C.fgMuted,
                    backgroundColor: C.card,
                    border: '1px solid var(--border)',
                    borderRadius: 4, padding: '1px 7px',
                  }}>
                    {hasConflictQueue ? totalConflicts : suggestions.length}
                  </span>
                </div>

                {/* Sort toggle — standard mode only */}
                {!hasConflictQueue && (
                  <div style={{
                    display: 'inline-flex', borderRadius: 6,
                    border: '1px solid var(--border)', overflow: 'hidden',
                  }}>
                    {(['severity', 'date'] as const).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setSortOrder(mode)}
                        style={{
                          fontSize: 11, fontWeight: 600,
                          padding: '4px 10px',
                          border: 'none', cursor: 'pointer',
                          transition: 'all 0.12s',
                          backgroundColor: sortOrder === mode ? C.teal : 'transparent',
                          color: sortOrder === mode ? '#fff' : C.fgMuted,
                          textTransform: 'capitalize',
                        }}
                        title={
                          mode === 'severity'
                            ? 'Sort by highest utilisation first'
                            : 'Sort by soonest conflict first'
                        }
                      >
                        {mode === 'severity' ? 'Severity' : 'Date'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Resolve all — moved to TOP of list (spec §6), before conflict items */}
              {hasConflictQueue && totalConflicts >= 2 && (
                <div style={{ flexShrink: 0 }}>
                  <div style={{ padding: '10px 0 4px' }}>
                    <ResolveAllPanel
                      conflictItems={externalConflictItems!}
                      resources={resources}
                      bookings={bookings}
                      onResolveAll={handleResolveAll}
                      resolvedAllWith={null}
                    />
                  </div>
                  {/* 0.5px divider below resolve-all (spec §6) */}
                  <div style={{
                    height: 0,
                    borderBottom: `0.5px solid ${VT.colBorder}`,
                    margin: '0 0 6px',
                  }} />
                </div>
              )}

              {/* Conflict items list */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '6px 10px 12px' }}>
                {hasConflictQueue
                  ? externalConflictItems!.map((item, idx) => (
                      <ConflictListItem
                        key={item.id}
                        item={item}
                        index={idx}
                        isActive={idx === activeConflictIdx && !isInResolveAllReview}
                        isResolved={resolvedIndices.has(idx)}
                        onSelect={() => {
                          setActiveConflictIdx(idx);
                          setEditingFromResolveAll(false);
                          resetConflictUI();
                        }}
                      />
                    ))
                  : sortedSuggestions.map((s) => (
                      <SuggestionListItem
                        key={s.atRiskResourceId}
                        suggestion={s}
                        isActive={suggestion?.atRiskResourceId === s.atRiskResourceId}
                        onSelect={onSelectSuggestion}
                      />
                    ))}
              </div>
            </div>

            {/* ── CENTRE + RIGHT — resolve-all review OR individual conflict */}
            {isInResolveAllReview ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ResolveAllReviewPanel
                  rows={resolveAllReviewRows}
                  candidateName={resolveAllCandidateName!}
                  onAcceptRow={handleAcceptResolveAllRow}
                  onEditRow={handleEditResolveAllRow}
                  onConfirmAll={handleConfirmResolveAll}
                  busy={isApplying}
                />
              </div>
            ) : (
              <>
                {/* ── CENTRE — recommendation + resolution + impact ──── */}
                <div style={{
                  flex: 1, minWidth: 0,
                  backgroundColor: 'var(--card)',
                  borderRight: `0.5px solid ${VT.colBorder}`,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden',
                }}>
                  <div style={{ flex: 1, padding: '16px 18px 0', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>

                    {/* ① Stat bar */}
                    <div style={{
                      display: 'flex', alignItems: 'stretch', gap: 0,
                      borderRadius: VT.radius, overflow: 'hidden',
                      backgroundColor: VT.colBg,
                      border: `0.5px solid ${VT.colBorder}`,
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '10px 14px', flex: 1, minWidth: 0,
                      }}>
                        <AlertTriangle size={14} style={{ color: C.amber, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, fontWeight: 600, color: C.fg, whiteSpace: 'nowrap' }}>
                          {Math.round(peakUtil)}% utilisation
                        </span>
                      </div>
                      <div style={{
                        padding: '10px 14px',
                        borderLeft: '1px solid var(--border)',
                        display: 'flex', alignItems: 'center',
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: C.fgMuted, whiteSpace: 'nowrap' }}>
                          <Clock size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                          {suggestion.affectedWeekStarts.length} week{suggestion.affectedWeekStarts.length !== 1 ? 's' : ''}
                          {suggestion.affectedWeekStarts.length > 0 &&
                            ` from ${format(suggestion.affectedWeekStarts[0], 'd MMM')}`}
                        </span>
                      </div>
                    </div>

                    {/* ② Recommendation — spec §8: no 0% primary */}
                    <div style={{
                      padding: '12px 14px',
                      borderRadius: VT.radius,
                      border: `0.5px solid ${VT.accentTealBorder}`,
                      backgroundColor: VT.accentTealBg,
                    }}>
                      <div style={{
                        fontSize: 11, fontWeight: 600, color: VT.accentTeal,
                        letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8,
                      }}>
                        Recommendation
                      </div>
                      {selectedResolution === 'reassign' && (
                        distributionPlacedNothing ? (
                          <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                            No resource has day-level room for these bookings, so there is no
                            reassignment to recommend — manual rebalancing (Split, Reduce or Delay)
                            is required for this conflict.
                            {excludedAtRiskNames.length > 0 && (
                              <>
                                {' '}
                                {excludedAtRiskNames.length} resource
                                {excludedAtRiskNames.length === 1 ? ' was' : 's were'} not
                                considered because {excludedAtRiskNames.length === 1 ? 'it is' : 'they are'}{' '}
                                already at burnout risk ({excludedAtRiskNames.join(', ')}).
                              </>
                            )}
                          </p>
                        ) : distributionSpread ? (
                          <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                            No single resource has capacity for the whole conflict, so it is
                            spread across <strong>{distributionTargetLabel}</strong> — each stays within
                            capacity for the affected dates. This is exactly what the calendar preview applies.
                          </p>
                        ) : (
                          <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                            Reassign to <strong>{distributionTargetLabel}</strong> — this resource
                            can absorb the full conflict within capacity for the affected dates,
                            zero timeline slip. This is exactly what the calendar preview applies.
                          </p>
                        )
                      )}
                      {selectedResolution === 'split' && (
                        <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                          Split hours between <strong>{suggestion.atRiskResourceName?.split(' ')[0]}</strong> and{' '}
                          <strong>{suggestion.candidateName?.split(' ')[0] ?? 'a second resource'}</strong> — no timeline slip,
                          utilisation drops to a safe level. Both work in parallel.
                        </p>
                      )}
                      {selectedResolution === 'reduce' && (
                        <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                          Reduce <strong>{suggestion.atRiskResourceName?.split(' ')[0]}</strong>'s weekly hours to bring
                          utilisation under 100%. Timeline extends — use this to model the scope conversation
                          with the project owner.
                        </p>
                      )}
                      {selectedResolution === 'delay' && (
                        recommendedDelay ? (
                          <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                            Delay the booking by{' '}
                            <strong>{recommendedDelay.delayWeeks} week{recommendedDelay.delayWeeks === 1 ? '' : 's'}</strong>
                            {' '}— {suggestion.atRiskResourceName} will have{' '}
                            {recommendedDelay.availablePct}% capacity available from{' '}
                            {format(recommendedDelay.weekStart, 'd MMM yyyy')}.
                            {!recommendedDelay.meetsTarget && (
                              <> No week is under 80% booked within the horizon, so this is the
                              best-available week rather than a genuinely free one.</>
                            )}
                            {' '}Check knock-on effects before committing.
                          </p>
                        ) : (
                          <p style={{ margin: 0, fontSize: 14, color: C.fg, lineHeight: 1.6 }}>
                            No later week could be assessed for {suggestion.atRiskResourceName}, so
                            there is no delay date to recommend.
                          </p>
                        )
                      )}
                    </div>

                    {/* ③ Resolution options — spec §9: 3px teal border when selected */}
                    <div>
                      <SectionLabel>Resolution options</SectionLabel>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {enabledOptions.map((opt) => {
                          const isSel = selectedResolution === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={() => setSelectedResolution(opt.id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                width: '100%', textAlign: 'left',
                                padding: '10px 12px',
                                borderRadius: isSel ? `0 ${VT.radius} ${VT.radius} 0` : VT.radius,
                                border: isSel ? 'none' : `0.5px solid ${VT.colBorder}`,
                                borderLeft: isSel ? `3px solid ${VT.accentTeal}` : `0.5px solid ${VT.colBorder}`,
                                backgroundColor: isSel ? VT.accentTealBg : 'white',
                                cursor: 'pointer', transition: 'all 0.15s',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSel) {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f5f5f5';
                                  (e.currentTarget as HTMLButtonElement).style.borderColor = VT.colBorderPrimary;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSel) {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'white';
                                  (e.currentTarget as HTMLButtonElement).style.borderColor = VT.colBorder;
                                }
                              }}
                            >
                              <span style={{ color: isSel ? VT.accentTeal : C.fgMuted, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                {opt.icon}
                              </span>
                              <span style={{ fontWeight: 600, fontSize: 14, color: isSel ? VT.accentTealText : C.fgMuted }}>
                                {opt.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ④ Impact metrics — spec §10: dynamic label + per-option values */}
                    <div>
                      <div style={{
                        fontSize: 13, fontWeight: 500,
                        color: C.fg, marginBottom: 10,
                      }}>
                        Impact of: <strong>{activeOption.label}</strong>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        <MetricTile label="Resource match"  value={activeOption.metrics.resourceMatch} accent />
                        <MetricTile label="Timeline impact" value={activeOption.metrics.timelineImpact} />
                        <MetricTile label="Risk level"      value={activeOption.metrics.riskLevel} />
                      </div>
                    </div>

                  </div>

                  {/* Footer — pinned */}
                  <div className="flex items-center gap-2.5 px-4 py-3 border-t bg-card shrink-0 mt-3.5">
                    <Button
                      size="sm"
                      // Reassign has no valid action when the distribution can
                      // place nothing — block Apply so we never commit a
                      // reassignment the redistribution plan already rejected.
                      disabled={
                        isApplying ||
                        (selectedResolution === 'reassign' && distributionPlacedNothing)
                      }
                      onClick={() => {
                        if (hasConflictQueue) {
                          // Queue mode — advance internally; do NOT call onApply
                          // because the parent's onApply closes the overlay
                          void handleMarkResolved();
                        } else {
                          onApply(buildAppliedSuggestion());
                          onClose();
                        }
                      }}
                      className="flex-[3] whitespace-nowrap"
                    >
                      {isApplying
                        ? 'Applying changes…'
                        : selectedResolution === 'reassign' && distributionPlacedNothing
                        ? 'No reassignment available'
                        : applyButtonLabel}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={hasConflictQueue ? handleRequestClose : onClose}
                      className="flex-1 whitespace-nowrap"
                    >
                      Close
                    </Button>
                  </div>
                </div>

                {/* ── RIGHT — context-aware flex weight per spec §12 ─── */}
                <div style={{
                  flex: rightColFlex, minWidth: 0,
                  backgroundColor: 'var(--muted)',
                  borderLeft: `0.5px solid ${VT.colBorder}`,
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '14px 18px 12px',
                    borderBottom: `0.5px solid ${VT.colBorder}`,
                    backgroundColor: 'var(--card)',
                    flexShrink: 0,
                  }}>
                    {selectedResolution === 'reassign' && (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
                          Available internal team members
                        </div>
                        <div style={{ fontSize: 13, color: C.fgMuted }}>
                          Matched by skills and availability
                        </div>
                      </>
                    )}
                    {selectedResolution === 'split' && (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
                          Split booking hours
                        </div>
                        <div style={{ fontSize: 13, color: C.fgMuted }}>
                          Drag the slider to set how many hours each person takes
                        </div>
                      </>
                    )}
                    {selectedResolution === 'reduce' && (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
                          Reduce booking hours
                        </div>
                        <div style={{ fontSize: 13, color: C.fgMuted }}>
                          Drag to model the impact on utilisation and timeline
                        </div>
                      </>
                    )}
                    {selectedResolution === 'delay' && (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.fg, marginBottom: 3 }}>
                          Available start dates for {suggestion.atRiskResourceName}
                        </div>
                        <div style={{ fontSize: 13, color: C.fgMuted }}>
                          Select when to reschedule — knock-on effects shown for each option
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px' }}>
                    {selectedResolution === 'reassign' && (
                      <TeamRequirementCard
                        suggestion={suggestion}
                        resources={resources}
                        bookings={bookings}
                        distribution={distribution}
                        acceptedId={acceptedCandidateId}
                        onAccept={handleAccept}
                        currentHoursPerWeek={currentHoursPerWeek}
                        // Keep the highlighted Top Pick anchored to a resource
                        // the distribution actually places work on: the single
                        // absorber when there is one, otherwise the primary
                        // spread target — never a coarse suggestion candidate
                        // the plan wouldn't use.
                        effectiveCandidateId={effectiveCandidateId ?? distributionTargets[0]?.id ?? null}
                      />
                    )}
                    {selectedResolution === 'split' && (
                      <SplitBookingPanel
                        suggestion={suggestion}
                        resources={resources}
                        acceptedId={acceptedCandidateId}
                        onAccept={handleAccept}
                        currentHoursPerWeek={currentHoursPerWeek}
                      />
                    )}
                    {selectedResolution === 'reduce' && (
                      <ReduceHoursPanel
                        suggestion={suggestion}
                        currentHoursPerWeek={currentHoursPerWeek}
                        onHoursChange={setReducedHours}
                      />
                    )}
                    {selectedResolution === 'delay' && (
                      <DelayOptionsPanel
                        suggestion={suggestion}
                        selectedSlot={selectedDelaySlot}
                        onSelectSlot={setSelectedDelaySlot}
                        nextAvailableWeeks={nextAvailableWeeks}
                        baseDate={delayBaseDate}
                        bookings={bookings}
                        resources={resources}
                      />
                    )}
                  </div>
                </div>
              </>
            )}

          </div>
        )}
      </div>

      {/* Discard confirmation dialog — rendered above overlay */}
      {showDiscardDialog && (
        <DiscardDialog
          resolvedCount={resolvedCount}
          onKeep={handleKeepResolving}
          onDiscard={handleDiscardConfirm}
        />
      )}
    </>
  );
};
